/**
 * SystemMapLivePage — "איך זה עובד": מפת המערכת החיה (DIGEMP-SYSTEM-MAP-LIVE-FRONTEND-092).
 *
 * Read-only explainability surface of the Digital Employees helpdesk flow: nine clickable system
 * stages, a clickable employee roster and drill-downs. Every live label is derived from an
 * existing authoritative backend contract; an unavailable or forbidden source degrades to an
 * explicit unknown — never to a fabricated zero or a false green.
 *
 * The map keeps three different truths apart:
 *  - יכולת (capability)  — what an employee is declared/measured able to do;
 *  - הרשאה (permission)  — what it is allowed to do (tier, allowlist, tool entitlement);
 *  - מוכנות כעת (readiness) — what it can actually do right now (enabled, waits, connections,
 *    crisis mode). An employee can be capable and permitted yet not ready.
 *
 * Onboarding/explainability only: the page never grants permissions, never starts execution and
 * never bypasses approvals — human decisions stay on the run/conversation screens it links to.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { useAuth } from "../context/AuthContext";
import { useAgentPermissions } from "../context/AgentPermissionsContext";
import api, {
  agentRunsApi,
  agentsApi,
  aiApi,
  connectionsApi,
  conversationsApi,
  knowledgeApi,
  knowledgeGapsApi,
  ticketsApi,
} from "../services/api";
import EmployeeAvatar, { STATUS_META } from "../components/EmployeeAvatar";
import EmployeeIdentityCard from "../components/EmployeeIdentityCard";
import {
  ExperienceTimeline,
  deriveAttentionItems,
  deriveOperatorSummary,
} from "../components/EmployeeWorkspace";
import {
  CHIP_CLS,
  SOURCE_STATES,
  classifySource,
  isSourceStale,
  statusChipMeta,
} from "../components/statusVocabulary";
import { useOnlineStatus, usePrefersReducedMotion, useReconnectRefetch } from "../components/useLiveState";
import { riskGradeLabel } from "../components/riskGradeVocabulary";
import { conversationStatusLabel } from "../components/conversationStatusVocabulary";
import { parseUtc } from "../utils/utcDate";
import "../styles/liveStates.css";

const OPEN_TICKET_CAP = 50;
const LIST_CAP = 5;
const LIVE_CHANGE_FLASH_MS = 1200;

// Live-status chips come from the shared vocabulary (components/statusVocabulary.js); this page
// never redefines a status word or a tone. "unknown" and "no_evidence" stay deliberately
// different: unknown = the source failed or is not readable; no_evidence = the source loaded and
// reported that no evidence exists on this build.

const DIMENSION_META = {
  capability: { label: "יכולת", cls: "bg-violet-50 text-violet-700 border-violet-200" },
  permission: { label: "הרשאה", cls: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  readiness: { label: "מוכנות כעת", cls: "bg-sky-50 text-sky-700 border-sky-200" },
};

const RISK_LABEL = { green: "סיכון נמוך", amber: "סיכון בינוני", red: "סיכון גבוה" };
const riskLabel = (risk) => RISK_LABEL[risk] || (typeof risk === "string" && risk.trim() ? risk : "לא ידוע");

const TEST_STATUS_LABEL = { ok: "נבדק — תקין", error: "נבדק — שגיאה" };
const testStatusLabel = (status) =>
  TEST_STATUS_LABEL[status] || (typeof status === "string" && status.trim() ? `נבדק — ${status}` : "טרם נבדק");

const STAGES = [
  {
    id: "intake",
    icon: "📨",
    label: "קליטה",
    hint: "כל פנייה שנכנסת (אימייל או קריאה ידנית) נפתחת כקריאה במחלקת השירות. העובדים הדיגיטליים אינם נוגעים בקריאה עד שמישהו מפעיל אותם עליה — הפעלה ידנית או טריגר מוגדר.",
  },
  {
    id: "routing",
    icon: "🧭",
    label: "ניתוב ובחירה",
    hint: "הקריאה משויכת לעובד דיגיטלי — לפי תחום, זמינות ועומס. השיוך נשאר החלטה של מפעיל; המפה מציגה מי מטפל במה ומה ממתין לאדם.",
  },
  {
    id: "conversation",
    icon: "💬",
    label: "שיחה והקשר",
    hint: "הערוץ שבו הלקוח, המפעיל והעובד הדיגיטלי מחליפים מידע; תוצאות ביצוע וראיות מתועדות לתוך השיחה.",
  },
  {
    id: "skills",
    icon: "🎯",
    label: "כישורים וידע",
    hint: "מה העובד יודע: תחומים מוצהרים ומיומנויות נמדדות, ובסיס הידע המאושר שממנו מותר לו לקחת פתרונות. הצהרה אינה מדידה ואינה הרשאה.",
  },
  {
    id: "connections",
    icon: "🔌",
    label: "חיבורים למערכות",
    hint: "המערכות החיצוניות שהעובדים נשענים עליהן (OpenClaw, TeamViewer, Microsoft 365 ומנוע הניתוח). חיבור מוגדר הוא תנאי הכרחי — אך אינו הרשאה ואינו ביצוע.",
  },
  {
    id: "approval",
    icon: "🛡️",
    label: "אישור",
    hint: "פעולה חיצונית או שינוי מצב אינם מתבצעים על ידי העובד לבדו: הם מוצגים לאישור אדם עם רמת הסיכון שלהם, וההמתנה נמדדת מרגע ההצעה.",
  },
  {
    id: "execution",
    icon: "⚙️",
    label: "ביצוע",
    hint: "שלושת מסלולי הביצוע — כלים/API, דפדפן ומרחוק. כל מסלול עובר דרך החיבורים המוגדרים ובכפוף למצב חירום ולמצב הפיילוט.",
  },
  {
    id: "verification",
    icon: "✅",
    label: "אימות וראיות",
    hint: "לא כל סיום נחשב הצלחה: רק עבודה שאושרה בידי טכנאי נחשבת מאומתת; השאר מוצגות כטרם אומתו או ככשל — לפי אותם כללים שהשרת מפעיל.",
  },
  {
    id: "learning",
    icon: "📈",
    label: "למידה וגדילה",
    hint: "ממשובי מפעילים, פערי ידע ופרופיל מקצועי — מה העובד למד ואיפה נדרש ידע חדש. שינויי כישורים נשמרים כגרסאות ומבוססים ראיות.",
  },
];

export function employeeReadinessChip(employee, { crisisActive = false } = {}) {
  if (!employee) return { key: "unknown", label: "לא ידוע" };
  if (crisisActive) return { key: "blocked", label: "חסום — מצב חירום" };
  if (employee.live === false) return { key: "unknown", label: "לא ידוע" };
  if (employee.is_enabled === false) return { key: "disabled", label: "כבוי" };
  if ((employee.awaiting_operator || 0) > 0) return { key: "approval_required", label: "ממתין למפעיל" };
  if (employee.current_work || (employee.workload?.active_runs || 0) > 0) return { key: "active", label: "מטפל כעת" };
  if ((employee.workload?.waiting_for_customer || 0) > 0) return { key: "idle", label: "ממתין ללקוח" };
  if ((employee.workload?.paused || 0) > 0) return { key: "idle", label: "מושהה" };
  const availability = STATUS_META[employee.availability_status];
  return { key: "idle", label: availability ? availability.label : employee.availability_status || "לא ידוע" };
}

const capabilityText = (employee) => {
  const declared = employee?.specialist_readiness?.declared_specialties;
  if (!Array.isArray(declared)) return "לא ידוע";
  if (declared.length === 0) return "טרם הוצהרו תחומים";
  return declared.length === 1 ? "תחום מוצהר אחד" : `תחומים מוצהרים: ${declared.length}`;
};

const permissionText = (agent) => {
  if (!agent) return "לא ידוע";
  const tools = agent.allowed_tools;
  if (Array.isArray(tools) && tools.length > 0) {
    return tools.length === 1 ? "רשימה מאושרת (כלי אחד)" : `רשימה מאושרת (${tools.length} כלים)`;
  }
  return "ללא הגבלת רשימה";
};

const bounded = (value, max = 160) => {
  const text = typeof value === "string" ? value.trim() : "";
  if (!text) return null;
  return text.length > max ? `${text.slice(0, max)}…` : text;
};

const fmtDateTime = (iso) => {
  if (!iso) return null;
  const date = parseUtc(iso);
  return Number.isNaN(date.getTime())
    ? null
    : date.toLocaleString("he-IL", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" });
};

const timeOf = (timestamp) => (timestamp ? new Date(timestamp).toLocaleTimeString("he-IL") : null);

// The exact stored instant behind a relative age. UTC is shown on purpose: a delayed or
// future-skewed record must stay readable as the source wrote it, and a relative "לפני N דק׳"
// alone cannot distinguish the two. `dateTime` carries the authoritative ISO instant.
const fmtSourceInstant = (iso) => {
  if (!iso) return null;
  const date = parseUtc(iso);
  if (Number.isNaN(date.getTime())) return null;
  return `${date.toISOString().slice(0, 16).replace("T", " ")} UTC`;
};

function SourceInstant({ at, label = "מועד המקור:" }) {
  const text = fmtSourceInstant(at);
  if (!text) return null;
  return (
    <span className="text-[10px] text-gray-400">
      {label}{" "}
      <time dateTime={at} dir="ltr" className="font-mono">{text}</time>
    </span>
  );
}

// Successor of the old loaded/loading suffix: a source whose last refresh failed after an earlier
// successful load keeps showing that payload — and now says exactly that, with the time of the
// successful load, instead of passing it off as current.
const sourceSuffix = (query) => {
  const state = classifySource(query);
  if (state.key === SOURCE_STATES.failed) return " (לא נטען)";
  if (state.key === SOURCE_STATES.stale) return ` (הרענון נכשל — הנתון מ־${timeOf(state.since)})`;
  if (state.key === SOURCE_STATES.fresh) return ` (נטען ${timeOf(state.since)})`;
  return " (טוען)";
};

const sourceFailed = (query) => query?.isError && !query?.data;

// ─── Live event flow (DIGEMP-FINISH-SYSTEM-MAP-LIVE-FLOW-151) ────────────────────────────────
//
// The motion surface of the map: the bounded transition feed of GET /api/agents/system-map
// (DIGEMP-FINISH-SYSTEM-MAP-TELEMETRY-148) rendered as a pipeline rail. A dot travels only when
// the server graded both the event itself (freshness) and its source family (state) as fresh;
// this page never re-grades freshness on its own. Stale, offline and unknown evidence renders
// static, a frozen snapshot (no network, failed refresh) stops moving, and
// prefers-reduced-motion swaps every traveling dot for the same information as a static
// equivalent instead of hiding it. Read-only: the feed contributes navigation links only.
// (DIGEMP-CLOSEOUT-SYSTEM-MAP-UX-209): the two freeze causes stay apart — a lost network names
// the automatic re-refresh on reconnect, a failed refresh names the retry — and the high-load
// bounds above cap the rendered list and the animated dots without dropping an event.

const FLOW_SOURCE_STAGE = {
  ticket_history: "intake",
  agent_runs: "execution",
  agent_connections: "connections",
};

// High-load bounds (DIGEMP-CLOSEOUT-SYSTEM-MAP-UX-209): the feed may hold up to event_cap events
// per family, so the rendered list is capped with its remainder disclosed, and only the newest
// FLOW_MOTION_DOT_CAP particles per stage run the infinite animation — every particle stays one
// real event, overflow particles stay truthful but static, and the cap is stated in words.
const FLOW_EVENT_LIST_CAP = 30;
const FLOW_MOTION_DOT_CAP = 12;
const FLOW_STAGE_SOURCE = {
  intake: "ticket_history",
  execution: "agent_runs",
  connections: "agent_connections",
};

// The 148 feed's own state vocabulary (fresh | stale | unknown | offline). "מעודכן"/"מיושן"/
// "לא ידוע" match the evidence-state words the delivered workspace surfaces use; offline is the
// feed's fourth, deliberately distinct state — a recorded failed test is not a quiet feed.
const FLOW_STATE_META = {
  fresh: { label: "מעודכן", cls: "bg-green-100 text-green-700 border-green-300" },
  stale: { label: "מיושן", cls: "bg-amber-100 text-amber-700 border-amber-300" },
  offline: { label: "מנותק — בדיקת חיבור נכשלה", cls: "bg-rose-100 text-rose-700 border-rose-300" },
  unknown: { label: "לא ידוע", cls: "bg-gray-100 text-gray-500 border-dashed border-gray-300" },
};
const FLOW_PARTICLE_TONE = {
  fresh: "bg-green-500",
  stale: "bg-amber-400",
  offline: "bg-rose-500",
  unknown: "bg-gray-300",
};

function FlowStateChip({ state }) {
  const meta = FLOW_STATE_META[state] || FLOW_STATE_META.unknown;
  return <span className={`${CHIP_CLS} ${meta.cls}`}>{meta.label}</span>;
}

// What one feed event may display: fresh only when the server graded both the event and its
// family fresh — one recent event can never upgrade an offline or unknown family, and anything
// unreadable fails closed to unknown. Same worst-of composition the feed itself applies to its
// source states.
function flowEventTruth(event, sourceState) {
  const resolved = sourceState || "unknown";
  const looksLive = event.freshness === "fresh" && resolved === "fresh";
  let state;
  if (looksLive) state = "fresh";
  else if (resolved === "offline") state = "offline";
  else if (resolved === "stale") state = "stale";
  else if (resolved === "unknown") state = "unknown";
  else state = event.freshness === "stale" ? "stale" : "unknown";
  return { looksLive, state };
}

function EventFlowSection({ flow, query, flowFrozen, flowOffline, prefersReducedMotion }) {
  const events = useMemo(() => (Array.isArray(flow?.events) ? flow.events : []), [flow]);
  const sources = useMemo(() => (Array.isArray(flow?.sources) ? flow.sources : []), [flow]);
  const integrations = Array.isArray(flow?.integrations) ? flow.integrations : [];

  const sourceStates = useMemo(
    () => new Map(sources.map((source) => [source.key, source.state])),
    [sources]
  );

  const particles = useMemo(() => {
    if (!flow) return [];
    const perStage = new Map();
    return events.flatMap((event, index) => {
      const stageId = FLOW_SOURCE_STAGE[event.source];
      // An unrecognized family stays in the list below but is never placed on the rail.
      if (!stageId) return [];
      const stageIndex = perStage.get(stageId) || 0;
      perStage.set(stageId, stageIndex + 1);
      const { looksLive, state } = flowEventTruth(event, sourceStates.get(event.source));
      return [{
        key: `${event.source}-${event.at}-${index}`,
        stageId,
        stageIndex,
        event,
        looksLive,
        state,
      }];
    });
  }, [flow, events, sourceStates]);

  const motionAllowed = !prefersReducedMotion && !flowFrozen;
  const liveCount = particles.filter((particle) => particle.looksLive).length;
  const traveling = motionAllowed && liveCount > 0;
  const motionCapped =
    motionAllowed && particles.some((particle) => particle.looksLive && particle.stageIndex >= FLOW_MOTION_DOT_CAP);

  let motionLabel = null;
  if (flow) {
    if (liveCount === 0) motionLabel = "אין תנועה כרגע — אין אירועים מעודכנים בזרימה.";
    else if (prefersReducedMotion) motionLabel = `מוצג מצב סטטי — ${liveCount} אירועים מעודכנים בזרימה (העדפת צמצום תנועה).`;
    else if (flowFrozen && flowOffline) motionLabel = `התנועה הושהתה — ${liveCount} אירועים מעודכנים בזרימה, אך הדפדפן מנותק מהרשת; עם חזרת החיבור הזרימה תתרענן אוטומטית.`;
    else if (flowFrozen) motionLabel = `התנועה הושהתה — ${liveCount} אירועים מעודכנים בזרימה, אך הרענון נכשל; הנתון מוקפא עד לרענון מוצלח.`;
    else motionLabel = `תנועה חיה — ${liveCount} אירועים מעודכנים בזרימה.${motionCapped
      ? ` להפחתת עומס התצוגה מונפשות עד ${FLOW_MOTION_DOT_CAP} נקודות בכל שלב; נקודות חיות מעבר לכך מוצגות סטטיות.`
      : ""}`;
  }

  const shownEvents = events.slice(0, FLOW_EVENT_LIST_CAP);

  return (
    <section
      aria-label="זרימת האירועים החיה"
      aria-busy={query.isLoading && !query.data}
      data-testid="system-map-event-flow"
      className="space-y-3"
    >
      {/* Travel keyframes start off-node and settle on it; the +X origin is the page's RTL
          upstream (right). The media guard duplicates the JS gate against a stale matchMedia. */}
      <style>{`
        @keyframes sm-flow-travel { 0% { transform: translateX(18px); opacity: 0; } 60% { opacity: 1; } 100% { transform: translateX(0); opacity: 1; } }
        @keyframes sm-flow-breathe { 0%, 100% { transform: translateX(0) scale(1); } 50% { transform: translateX(0) scale(1.4); } }
        .sm-flow-particle-traveling {
          animation: sm-flow-travel 1s ease-out var(--sm-flow-delay, 0ms) both,
                     sm-flow-breathe 2.6s ease-in-out calc(var(--sm-flow-delay, 0ms) + 1s) infinite;
        }
        @media (prefers-reduced-motion: reduce) { .sm-flow-particle-traveling { animation: none; } }
      `}</style>

      <div className="flex items-center justify-between gap-2 flex-wrap">
        <h2 className="text-lg font-semibold text-gray-800">זרימת האירועים החיה</h2>
        <p className="text-[11px] text-gray-400">
          תנועה מהזרימה האמיתית בלבד: נקודה נעה רק כשאירוע והמקור שלו מסומנים מעודכנים בשרת.
          ראיה מיושנת, מנותקת או לא ידועה מוצגת סטטית — לעולם לא כתנועה חיה. לקריאה בלבד.
        </p>
      </div>

      {!flow && query.isLoading && <p className="text-sm text-gray-400">טוען זרימת אירועים…</p>}
      {!flow && !query.isLoading && <FailNote>מקור זרימת האירועים לא נטען — לא מוצגת תנועה.</FailNote>}

      {flow && (
        <>
          {motionLabel && (
            <p data-testid="system-map-flow-motion" className="text-xs text-gray-500">{motionLabel}</p>
          )}

          <div
            data-testid="system-map-flow-rail"
            data-flow-motion={traveling ? "active" : "none"}
            aria-hidden="true"
            className="flex flex-wrap items-start justify-between gap-2 rounded-xl border border-gray-200 bg-gray-50/60 p-3"
          >
            {STAGES.map((stage) => {
              const stageParticles = particles.filter((particle) => particle.stageId === stage.id);
              const sourceKey = FLOW_STAGE_SOURCE[stage.id];
              return (
                <div
                  key={stage.id}
                  data-flow-stage={stage.id}
                  data-testid={`system-map-flow-stage-${stage.id}`}
                  data-flow-particles={stageParticles.length}
                  className="flex flex-col items-center gap-1 text-center min-w-[4.5rem]"
                >
                  <span className="text-base">{stage.icon}</span>
                  <span className="text-[11px] text-gray-600">{stage.label}</span>
                  <span dir="ltr" className="flex items-center justify-center gap-0.5 min-h-[0.6rem]">
                    {stageParticles.map((particle) => {
                      const animated =
                        particle.looksLive && motionAllowed && particle.stageIndex < FLOW_MOTION_DOT_CAP;
                      return (
                        <span
                          key={particle.key}
                          data-testid="system-map-flow-particle"
                          data-source={particle.event.source}
                          data-kind={particle.event.kind}
                          data-state={particle.state}
                          data-flow-live={particle.looksLive ? "true" : "false"}
                          data-motion={animated ? "traveling" : "static"}
                          style={{ "--sm-flow-delay": `${Math.min(particle.stageIndex, FLOW_MOTION_DOT_CAP) * 90}ms` }}
                          className={`inline-block w-2 h-2 rounded-full ${FLOW_PARTICLE_TONE[particle.state]}${
                            animated ? " sm-flow-particle-traveling" : ""
                          }`}
                        />
                      );
                    })}
                  </span>
                  {sourceKey && (
                    <span dir="ltr" className="font-mono text-[9px] text-gray-400">{sourceKey}</span>
                  )}
                </div>
              );
            })}
          </div>

          <p className="flex flex-wrap items-center gap-x-2 text-[11px] text-gray-400">
            <span>
              חלון הטריות: {flow.freshness_budget_hours} שעות · מכסת הסריקה: עד {flow.event_cap} אירועים לכל מקור
              {flow.truncated ? " · הרשימה נחתכה למכסה — ייתכנו אירועים נוספים" : ""}
            </span>
            <SourceInstant at={flow.generated_at} label="נבנה:" />
          </p>

          <div className="space-y-1">
            <h3 className="text-sm font-semibold text-gray-700">האירועים שנסרקו (חדש→ישן)</h3>
            {events.length === 0 ? (
              <p className="text-xs text-gray-500">אין אירועים מתועדים בחלון הסריקה — אין תנועה להצגה.</p>
            ) : (
              <>
                <ol data-testid="system-map-flow-events" className="space-y-1">
                  {shownEvents.map((event, index) => (
                    <li
                      key={`${event.source}-${event.at}-${index}`}
                      data-at={event.at}
                      data-source={event.source}
                      className="flex flex-wrap items-center gap-2 text-sm"
                    >
                      <FlowStateChip state={flowEventTruth(event, sourceStates.get(event.source)).state} />
                      <span className="text-gray-800">{bounded(event.label, 120) || event.kind}</span>
                      {event.from_value && event.to_value && (
                        <span className="text-xs text-gray-500">
                          מ־<span dir="auto">{bounded(event.from_value, 60)}</span> ל־<span dir="auto">{bounded(event.to_value, 60)}</span>
                        </span>
                      )}
                      {event.actor && <span className="text-xs text-gray-500">על ידי {bounded(event.actor, 80)}</span>}
                      {typeof event.age_minutes === "number" && (
                        <span className="text-[11px] text-gray-400">לפני {event.age_minutes} דק׳</span>
                      )}
                      <SourceInstant at={event.at} />
                      <span dir="ltr" className="font-mono text-[10px] text-gray-400">{event.source}</span>
                      <RunLink runId={event.ref?.run_id} />
                      <TicketLink ticketId={event.ref?.ticket_id} ticketNumber={event.ref?.ticket_number} />
                    </li>
                  ))}
                </ol>
                {events.length > shownEvents.length && (
                  <p className="text-[11px] text-gray-400">
                    מוצגים {shownEvents.length} האירועים החדשים ביותר מתוך {events.length} שנסרקו — להגבלת עומס התצוגה.
                  </p>
                )}
              </>
            )}
          </div>

          <div className="space-y-1">
            <h3 className="text-sm font-semibold text-gray-700">מצב מקורות הזרימה</h3>
            <ul data-testid="system-map-flow-sources" className="grid gap-2 md:grid-cols-3">
              {sources.map((source) => (
                <li
                  key={source.key}
                  data-testid={`system-map-flow-source-${source.key}`}
                  data-last-event-at={source.last_event_at || ""}
                  className="rounded-lg border border-gray-200 bg-white p-2 space-y-1"
                >
                  <div className="flex flex-wrap items-center gap-1.5">
                    <FlowStateChip state={source.state} />
                    <span className="text-xs text-gray-700">{source.label}</span>
                  </div>
                  <p className="text-[11px] text-gray-500">
                    אירועים בחלון: {source.event_count}
                    {typeof source.age_minutes === "number" ? ` · החדש ביותר לפני ${source.age_minutes} דק׳` : ""}
                    {source.truncated ? " · הוצגה מכסה חלקית" : ""}
                  </p>
                  <SourceInstant at={source.last_event_at} label="החדש ביותר:" />
                  {source.note && <p className="text-[11px] text-gray-400">{source.note}</p>}
                </li>
              ))}
            </ul>
          </div>

          {integrations.length > 0 && (
            <div className="space-y-1">
              <h3 className="text-sm font-semibold text-gray-700">ראיות בדיקת חיבורים</h3>
              <ul data-testid="system-map-flow-integrations" className="space-y-1">
                {integrations.map((item) => (
                  <li
                    key={item.connection_id}
                    data-testid={`system-map-flow-integration-${item.connection_id}`}
                    data-state={item.state}
                    data-last-tested-at={item.last_tested_at || ""}
                    className="flex flex-wrap items-center gap-2 text-xs text-gray-700"
                  >
                    <FlowStateChip state={item.state} />
                    <span>{bounded(item.display_name, 60) || bounded(item.system_type, 40) || "חיבור"}</span>
                    {item.last_test_status && (
                      <span dir="ltr" className="font-mono text-[10px] text-gray-400">{bounded(item.last_test_status, 40)}</span>
                    )}
                    {typeof item.age_minutes === "number" && (
                      <span className="text-[11px] text-gray-400">לפני {item.age_minutes} דק׳</span>
                    )}
                    <SourceInstant at={item.last_tested_at} label="נבדק:" />
                  </li>
                ))}
              </ul>
            </div>
          )}

          <p className="text-[11px] text-gray-400">
            מקור הזרימה: <span dir="ltr" className="font-mono">GET /api/agents/system-map</span>
            {sourceSuffix(query)}
          </p>
        </>
      )}
    </section>
  );
}

// ─── Operator focus: ticket / worker position in the flow (DIGEMP-FINISH-SYSTEM-MAP-FOCUS-160) ─
//
// Bounded drill-downs that answer "where is this ticket / this worker in the flow, what is
// blocked, and which source proves it" from data the page already holds: the authoritative event
// flow (148/151) plus the recorded waits of GET /api/agent-runs/pending-approvals and
// GET /api/agents/workforce-overview. Selecting a focus issues no request, opens no write path
// and adds no backend authority. Every displayed fact cites its source; a family or query that is
// stale / offline / unknown / failed is shown as exactly that and never as live motion or a
// fabricated current state. The two native selects are the only controls — labeled, keyboard-
// operable, RTL-inheriting — and the lists are bounded with their caps disclosed.
// (DIGEMP-CLOSEOUT-SYSTEM-MAP-UX-209): every blocked reason — the disabled flag, operator waits,
// disconnected or stale connections, crisis mode — carries the endpoint that proves it, and the
// frozen disclosure separates a lost network from a failed refresh instead of guessing between
// them.

const FOCUS_LIST_CAP = 8;
const FOCUS_EVENT_CAP = 6;

const FOCUS_TICKET_FAMILIES = ["ticket_history", "agent_runs"];
const FOCUS_WORKER_FAMILIES = ["agent_runs", "agent_connections"];

const STAGE_LABEL_BY_ID = Object.fromEntries(STAGES.map((stage) => [stage.id, stage.label]));

// Words for the non-feed queries the panels cite, derived with the shared classifySource ladder.
// They stay separate from the feed's server-graded vocabulary on purpose: a held react-query
// payload and a server-graded stale event are different facts.
const QUERY_TRUTH_META = {
  fresh: { label: "מעודכן", cls: FLOW_STATE_META.fresh.cls },
  stale: { label: "מיושן — הנתון מהטעינה המוצלחת האחרונה", cls: FLOW_STATE_META.stale.cls },
  failed: { label: "לא נטען", cls: FLOW_STATE_META.unknown.cls },
  loading: { label: "טוען…", cls: FLOW_STATE_META.unknown.cls },
};

function QueryTruthChip({ query }) {
  const meta = QUERY_TRUTH_META[classifySource(query).key] || QUERY_TRUTH_META.failed;
  return <span className={`${CHIP_CLS} ${meta.cls}`}>{meta.label}</span>;
}

function SourceCite({ name, query }) {
  return (
    <span className="inline-flex flex-wrap items-center gap-1 text-[10px] text-gray-400">
      מקור: <span dir="ltr" className="font-mono">{name}</span>
      {query ? <QueryTruthChip query={query} /> : null}
    </span>
  );
}

const eventCountText = (count) => (count === 1 ? "אירוע אחד" : `${count} אירועים`);

const ticketFocusLabel = (option) => (option.number == null ? "קריאה ללא מספר" : `קריאה #${option.number}`);

// The bounded ticket list an operator can focus on: every ticket an already-loaded authoritative
// source names. Ordering is operator-first: a ticket with a recorded wait (approval or workforce
// attention) is offered before a ticket that only appears in the newest flow events, so a blocked
// ticket can never fall out of the bounded option list behind fresher traffic. The flow refs keep
// their newest-first order, then recorded current work fills the remainder. Nothing is queried for
// this list and nothing is synthesized; the cap is applied by the caller and disclosed in the UI.
function buildTicketFocusOptions({ flow, approvals, attention, employees }) {
  const seen = new Map();
  const add = (id, number) => {
    if (id == null || id === "") return;
    const key = String(id);
    if (seen.has(key)) return;
    seen.set(key, { id: key, number: number == null ? null : number });
  };
  ((approvals && approvals.pending) || []).forEach((item) => add(item.ticket_id, item.ticket_number));
  (Array.isArray(attention) ? attention : []).forEach((item) => add(item.ticket_id, item.ticket_number));
  const events = flow && Array.isArray(flow.events) ? flow.events : [];
  events.forEach((event) => add(event.ref?.ticket_id, event.ref?.ticket_number));
  (employees || []).forEach((employee) => add(employee.current_work?.ticket_id, employee.current_work?.ticket_number));
  return Array.from(seen.values());
}

// Worker options follow the same rule: a worker with a recorded block or active work is offered
// before an idle one, so the bounded selector still reaches the operators who need attention.
// Order is stable inside a rank (the roster's own order is preserved).
function buildWorkerFocusOptions(employeeNodes, employees) {
  const liveById = new Map((employees || []).map((employee) => [String(employee.agent_id), employee]));
  const rankOf = (node) => {
    const live = liveById.get(String(node.agent_id)) || node;
    if (
      live.is_enabled === false ||
      (live.awaiting_operator || 0) > 0 ||
      (live.workload?.paused || 0) > 0
    ) {
      return 0;
    }
    if (live.current_work || (live.workload?.active_runs || 0) > 0) return 1;
    return 2;
  };
  return (employeeNodes || [])
    .map((node, index) => ({ node, index, rank: rankOf(node) }))
    .sort((left, right) => left.rank - right.rank || left.index - right.index)
    .map(({ node }) => ({ id: node.agent_id, name: node.name }));
}

// A native select whose current value is not among its options renders blank. When a refresh
// pushes the selected ticket/worker outside the bounded slice, retain exactly that option so the
// control keeps showing what is actually focused (never silently looks like "nothing selected").
function withSelectedOption(options, selectedId, cap) {
  const shown = options.slice(0, cap);
  if (!selectedId || shown.some((option) => option.id === selectedId)) return shown;
  const selected = options.find((option) => option.id === selectedId);
  return selected ? [...shown, selected] : shown;
}

function FocusEventRow({ event, truth, testId }) {
  return (
    <li
      data-testid={testId}
      data-state={truth.state}
      data-source={event.source}
      data-at={event.at}
      className="flex flex-wrap items-center gap-2 text-sm"
    >
      <FlowStateChip state={truth.state} />
      <span className="text-gray-800">{bounded(event.label, 120) || event.kind}</span>
      {event.from_value && event.to_value && (
        <span className="text-xs text-gray-500">
          מ־<span dir="auto">{bounded(event.from_value, 60)}</span> ל־<span dir="auto">{bounded(event.to_value, 60)}</span>
        </span>
      )}
      {event.actor && <span className="text-xs text-gray-500">על ידי {bounded(event.actor, 80)}</span>}
      {typeof event.age_minutes === "number" && (
        <span className="text-[11px] text-gray-400">לפני {event.age_minutes} דק׳</span>
      )}
      <SourceInstant at={event.at} />
      <span dir="ltr" className="font-mono text-[10px] text-gray-400">{event.source}</span>
      <RunLink runId={event.ref?.run_id} />
    </li>
  );
}

function FocusBlockList({ items, blockTestId = "system-map-focus-block" }) {
  return (
    <ul className="space-y-1.5">
      {items.filter(Boolean).map((item, index) => (
        <li key={index} data-testid={blockTestId} className={item.cls}>{item.node}</li>
      ))}
    </ul>
  );
}

function FocusSourceRows({ rows, testIdPrefix }) {
  return (
    <ul className="space-y-1">
      {rows.map((row) => (
        <li
          key={row.key}
          data-testid={`${testIdPrefix}-${row.key}`}
          data-last-event-at={row.meta?.last_event_at || ""}
          className="flex flex-wrap items-center gap-2 text-xs text-gray-600"
        >
          <FlowStateChip state={row.meta ? row.meta.state : "unknown"} />
          <span>{row.meta ? row.meta.label : row.key}</span>
          <span className="text-gray-400">{row.countText}</span>
          {row.meta?.truncated && <span className="text-gray-400">· המקור נחתך במכסת הסריקה</span>}
          <SourceInstant at={row.meta?.last_event_at} label="אחרון:" />
        </li>
      ))}
    </ul>
  );
}

function TicketFocusPanel({ ticket, flow, sourceStates, approvals, approvalsQuery, workforceQuery, attention }) {
  const events = useMemo(() => {
    const list = flow && Array.isArray(flow.events) ? flow.events : [];
    return list.filter((event) => event.ref?.ticket_id != null && String(event.ref.ticket_id) === ticket.id);
  }, [flow, ticket.id]);

  const newest = events.length > 0 ? events[0] : null;
  const newestTruth = newest ? flowEventTruth(newest, sourceStates.get(newest.source)) : null;
  const shownEvents = events.slice(0, FOCUS_EVENT_CAP);
  const label = ticketFocusLabel(ticket);
  // The backend's bounded window can cut the feed even when this ticket's family was not capped.
  // In that case an absent event is "not retained in the window", never "never recorded".
  const windowBounded = !!(flow && flow.truncated);

  const familyRows = FOCUS_TICKET_FAMILIES.map((key) => {
    const count = events.filter((event) => event.source === key).length;
    let countText;
    if (!flow) countText = "מקור הזרימה לא נטען — אין נתון";
    else if (count === 0) {
      countText = windowBounded
        ? "לא נשמרו אירועים לקריאה זו בחלון הזרימה"
        : "אין אירועים לקריאה זו בחלון";
    } else countText = `${eventCountText(count)} לקריאה זו בחלון הזרימה`;
    return {
      key,
      meta: (flow?.sources || []).find((source) => source.key === key) || null,
      countText,
    };
  });

  const approvalItems = ((approvals && approvals.pending) || []).filter(
    (item) => item.ticket_id != null && String(item.ticket_id) === ticket.id
  );
  const attentionItems = attention.filter(
    (item) => item.ticket_id != null && String(item.ticket_id) === ticket.id
  );
  const unclearSources = [
    { key: "approvals", name: "GET /api/agent-runs/pending-approvals", query: approvalsQuery },
    { key: "workforce", name: "GET /api/agents/workforce-overview", query: workforceQuery },
  ].filter((source) => classifySource(source.query).key !== SOURCE_STATES.fresh);
  const hasBlockers = approvalItems.length > 0 || attentionItems.length > 0;
  const absenceClaimAllowed = !hasBlockers && unclearSources.length === 0;

  const blockItems = [
    ...approvalItems.map((item) => ({
      cls: "rounded-lg border border-blue-100 bg-blue-50/50 p-2 space-y-1",
      node: (
        <>
          <p className="text-sm text-gray-800">ממתין להחלטת אישור לפני המשך העבודה.</p>
          {item.pending_action && (
            <p className="text-xs text-gray-600">
              {bounded(item.pending_action.description, 120)} · {riskLabel(item.pending_action.risk_level)}
            </p>
          )}
          {fmtSourceInstant(item.wait_started_at) && (
            <p className="text-[10px] text-gray-400">
              <SourceInstant at={item.wait_started_at} label="ההמתנה החלה:" />
            </p>
          )}
          <p className="flex flex-wrap items-center gap-2 text-xs">
            <RunLink runId={item.run_id} />
            <SourceCite name="GET /api/agent-runs/pending-approvals" query={approvalsQuery} />
          </p>
        </>
      ),
    })),
    ...attentionItems.map((item) => ({
      cls: "rounded-lg border border-amber-100 bg-amber-50/50 p-2 space-y-1",
      node: (
        <>
          <p className="text-sm text-gray-800">
            {bounded(item.label, 120) || "פריט הממתין לאדם"}
            {item.agent_name ? ` — ${bounded(item.agent_name, 60)}` : ""}
          </p>
          <p className="flex flex-wrap items-center gap-2 text-xs">
            <RunLink runId={item.run_id} />
            <SourceCite name="GET /api/agents/workforce-overview" query={workforceQuery} />
          </p>
        </>
      ),
    })),
    ...unclearSources.map((source) => ({
      cls: "flex flex-wrap items-center gap-2 text-xs text-gray-600",
      node: (
        <>
          <QueryTruthChip query={source.query} />
          <span>
            מקור <span dir="ltr" className="font-mono">{source.name}</span> — ייתכן גורם חוסם שאינו מוצג.
          </span>
        </>
      ),
    })),
  ];

  return (
    <section
      aria-label={`מיקוד קריאה — ${label}`}
      data-testid="system-map-focus-ticket"
      className="rounded-xl border border-indigo-200 bg-white shadow-sm p-4 space-y-3"
    >
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h3 className="text-base font-semibold text-gray-800">{label}</h3>
        <TicketLink ticketId={ticket.id} label="למסך הקריאה" />
      </div>

      <div data-testid="system-map-focus-ticket-position" className="space-y-1.5">
        <h4 className="text-sm font-semibold text-gray-700">מיקום בזרימה</h4>
        {newestTruth ? (
          <p
            data-testid="system-map-focus-ticket-newest"
            data-at={newest.at}
            className="flex flex-wrap items-center gap-1.5 text-sm text-gray-700"
          >
            <FlowStateChip state={newestTruth.state} />
            <span>{bounded(newest.label, 120) || newest.kind}</span>
            <span data-testid="system-map-focus-ticket-stage" className="text-xs text-gray-500">
              · שלב {STAGE_LABEL_BY_ID[FLOW_SOURCE_STAGE[newest.source]] || "לא מזוהה"}
            </span>
            {typeof newest.age_minutes === "number" && (
              <span className="text-[11px] text-gray-400">לפני {newest.age_minutes} דק׳</span>
            )}
            <SourceInstant at={newest.at} />
            <span dir="ltr" className="font-mono text-[10px] text-gray-400">{newest.source}</span>
            <RunLink runId={newest.ref?.run_id} />
          </p>
        ) : (
          <p className="text-sm text-gray-500">
            {!flow
              ? "מקור הזרימה לא נטען — המיקום בזרימה אינו ידוע."
              : windowBounded
                ? "לא נשמרו אירועים לקריאה זו בחלון הזרימה החסום — ייתכן שהמיקום מחוץ לחלון ואינו ידוע."
                : "לא תועדו אירועים לקריאה זו בחלון הסריקה — המיקום בזרימה אינו ידוע."}
          </p>
        )}
        {newestTruth && !newestTruth.looksLive && (
          <p className="text-xs text-gray-500">
            הראיה החדשה ביותר אינה מסומנת מעודכנת בשרת — המצב כפי שתועד, לא כעת.
          </p>
        )}
        {familyRows.some((row) => row.key && events.some((event) => event.source === row.key)) && (
          <p className="flex flex-wrap items-center gap-2 text-xs text-gray-600">
            {familyRows
              .filter((row) => events.some((event) => event.source === row.key))
              .map((row) => (
                <span
                  key={row.key}
                  data-testid={`system-map-focus-ticket-stagecount-${row.key}`}
                  className="flex items-center gap-1"
                >
                  {STAGE_LABEL_BY_ID[FLOW_SOURCE_STAGE[row.key]] || row.key}:{" "}
                  {eventCountText(events.filter((event) => event.source === row.key).length)}
                  <FlowStateChip state={row.meta ? row.meta.state : "unknown"} />
                </span>
              ))}
          </p>
        )}
      </div>

      <div data-testid="system-map-focus-ticket-blocked" className="space-y-1.5">
        <h4 className="text-sm font-semibold text-gray-700">חסימות והמתנות</h4>
        <FocusBlockList items={blockItems} blockTestId="system-map-focus-ticket-block" />
        {absenceClaimAllowed && (
          <p className="text-xs text-gray-500">אין רשומת המתנה מתועדת לקריאה זו במקורות שנבדקו.</p>
        )}
      </div>

      <div data-testid="system-map-focus-ticket-trail" className="space-y-1.5">
        <h4 className="text-sm font-semibold text-gray-700">המסלול שתועד (חדש→ישן)</h4>
        {shownEvents.length === 0 ? (
          <p className="text-xs text-gray-500">
            {!flow
              ? "מקור הזרימה לא נטען — המסלול אינו מוצג."
              : windowBounded
                ? "לא נשמרו אירועים לקריאה זו בחלון הזרימה החסום — ייתכן שהמסלול נמשך מחוץ לחלון."
                : "לא תועדו אירועים לקריאה זו בחלון הסריקה."}
          </p>
        ) : (
          <>
            <ol className="space-y-1">
              {shownEvents.map((event, index) => (
                <FocusEventRow
                  key={`${event.source}-${event.at}-${index}`}
                  event={event}
                  truth={flowEventTruth(event, sourceStates.get(event.source))}
                  testId="system-map-focus-ticket-event"
                />
              ))}
            </ol>
            {events.length > shownEvents.length && (
              <p className="text-[11px] text-gray-400">
                מוצגים {shownEvents.length} האירועים החדשים ביותר מתוך {events.length} שנשמרו לקריאה זו בחלון הזרימה.
              </p>
            )}
          </>
        )}
      </div>

      <div data-testid="system-map-focus-ticket-sources" className="space-y-1">
        <h4 className="text-sm font-semibold text-gray-700">מקורות הראיות לקריאה זו</h4>
        <FocusSourceRows rows={familyRows} testIdPrefix="system-map-focus-ticket-source" />
        <p className="text-[11px] text-gray-400">
          כל פרט נשען על הרשומה האמינה שממנה נקרא; תווית המצב נקבעת בשרת.{" "}
          <span dir="ltr" className="font-mono">GET /api/agents/system-map</span>
        </p>
      </div>
    </section>
  );
}

function WorkerFocusPanel({ node, flow, flowQuery, sourceStates, workforceEmployee, workforceQuery, agentsQuery, approvals, approvalsQuery, attention, crisisActive, crisisQuery, canManage }) {
  const live = node.live !== false;
  const readiness = employeeReadinessChip(workforceEmployee || node, { crisisActive });
  const currentWork = live ? node.current_work : null;
  const currentRunId = currentWork?.run_id != null ? String(currentWork.run_id) : null;

  const runEvents = useMemo(() => {
    const list = flow && Array.isArray(flow.events) ? flow.events : [];
    if (!currentRunId) return [];
    return list.filter((event) => event.ref?.run_id != null && String(event.ref.run_id) === currentRunId);
  }, [flow, currentRunId]);

  const integrations = useMemo(() => {
    const list = flow && Array.isArray(flow.integrations) ? flow.integrations : [];
    return list.filter((item) => item.agent_id != null && String(item.agent_id) === node.agent_id);
  }, [flow, node.agent_id]);

  const newestRunEvent = runEvents.length > 0 ? runEvents[0] : null;
  const newestRunTruth = newestRunEvent
    ? flowEventTruth(newestRunEvent, sourceStates.get(newestRunEvent.source))
    : null;
  const shownRunEvents = runEvents.slice(0, FOCUS_EVENT_CAP);
  const shownIntegrations = integrations.slice(0, FOCUS_EVENT_CAP);
  const windowBounded = !!(flow && flow.truncated);

  const attentionItems = attention.filter((item) => item.agent_id === node.agent_id);
  const approvalItems = ((approvals && approvals.pending) || []).filter((item) => item.agent_id === node.agent_id);
  const offlineIntegrations = integrations.filter((item) => item.state === "offline");
  const staleIntegrations = integrations.filter((item) => item.state === "stale");
  const awaitingOperator = workforceEmployee?.awaiting_operator || 0;
  const pausedRuns = workforceEmployee?.workload?.paused || 0;

  const unclearSources = [
    { key: "workforce", name: "GET /api/agents/workforce-overview", query: workforceQuery, note: "ייתכן מידע חסר בנוגע להמתנות." },
    { key: "approvals", name: "GET /api/agent-runs/pending-approvals", query: approvalsQuery, note: "ייתכן מידע חסר בנוגע להמתנות." },
    { key: "flow", name: "GET /api/agents/system-map", query: flowQuery, note: "ייתכן מידע חסר בנוגע לחסימות חיבור ולמהלך הריצה." },
  ].filter((source) => classifySource(source.query).key !== SOURCE_STATES.fresh);
  const hasBlockers =
    approvalItems.length > 0 ||
    attentionItems.length > 0 ||
    offlineIntegrations.length > 0 ||
    staleIntegrations.length > 0 ||
    node.is_enabled === false ||
    pausedRuns > 0 ||
    (crisisActive && canManage);
  const absenceClaimAllowed = !hasBlockers && unclearSources.length === 0 && live;

  const familyRows = FOCUS_WORKER_FAMILIES.map((key) => {
    const count = key === "agent_runs" ? runEvents.length : integrations.length;
    let countText;
    if (!flow) countText = "מקור הזרימה לא נטען — אין נתון";
    else if (key === "agent_runs") {
      if (count > 0) countText = `${eventCountText(count)} לריצה הנוכחית בחלון הזרימה`;
      else if (currentRunId) {
        countText = windowBounded
          ? "לא נשמרו אירועים לריצה זו בחלון הזרימה"
          : "אין אירועים לריצה זו בחלון";
      } else countText = "אין ריצה נוכחית מתועדת";
    } else if (count > 0) countText = `${eventCountText(count)} של בדיקות חיבור בחלון הזרימה`;
    else {
      countText = windowBounded
        ? "לא נשמרו בדיקות חיבור בחלון הזרימה"
        : "אין בדיקות חיבור מתועדות בחלון";
    }
    return {
      key,
      meta: (flow?.sources || []).find((source) => source.key === key) || null,
      countText,
    };
  });

  const blockItems = [
    node.is_enabled === false && {
      cls: "text-sm text-gray-700 space-y-0.5",
      node: (
        <>
          <p>העובד כבוי — לא יקבל עבודה חדשה עד שיופעל.</p>
          <SourceCite
            name={live ? "GET /api/agents/workforce-overview" : "GET /api/agents/"}
            query={live ? workforceQuery : agentsQuery}
          />
        </>
      ),
    },
    live && awaitingOperator > 0 && {
      cls: "text-sm text-gray-700 space-y-0.5",
      node: (
        <>
          <p>פריטים הממתינים להחלטת מפעיל לפני המשך: {awaitingOperator}</p>
          <SourceCite name="GET /api/agents/workforce-overview" query={workforceQuery} />
        </>
      ),
    },
    live && pausedRuns > 0 && {
      cls: "text-sm text-gray-700 space-y-0.5",
      node: (
        <>
          <p>ריצות מושהות: {pausedRuns}</p>
          <SourceCite name="GET /api/agents/workforce-overview" query={workforceQuery} />
        </>
      ),
    },
    ...attentionItems.map((item) => ({
      cls: "rounded-lg border border-amber-100 bg-amber-50/50 p-2 space-y-1",
      node: (
        <>
          <p className="text-sm text-gray-800">
            {bounded(item.label, 120) || "פריט הממתין לאדם"}
            {item.ticket_number != null ? ` · קריאה #${item.ticket_number}` : ""}
          </p>
          <p className="flex flex-wrap items-center gap-2 text-xs">
            <RunLink runId={item.run_id} />
            <SourceCite name="GET /api/agents/workforce-overview" query={workforceQuery} />
          </p>
        </>
      ),
    })),
    ...approvalItems.map((item) => ({
      cls: "rounded-lg border border-blue-100 bg-blue-50/50 p-2 space-y-1",
      node: (
        <>
          <p className="text-sm text-gray-800">
            צעד מוצע ממתין לאישור
            {item.ticket_number != null ? ` — קריאה #${item.ticket_number}` : ""}
          </p>
          {item.pending_action && (
            <p className="text-xs text-gray-600">
              {bounded(item.pending_action.description, 120)} · {riskLabel(item.pending_action.risk_level)}
            </p>
          )}
          <p className="flex flex-wrap items-center gap-2 text-xs">
            <RunLink runId={item.run_id} />
            <SourceCite name="GET /api/agent-runs/pending-approvals" query={approvalsQuery} />
          </p>
        </>
      ),
    })),
    ...offlineIntegrations.map((item) => ({
      cls: "rounded-lg border border-rose-100 bg-rose-50/50 p-2 space-y-1",
      node: (
        <>
          <p className="flex flex-wrap items-center gap-2 text-sm text-gray-700">
            <FlowStateChip state="offline" />
            <span>
              חיבור מנותק: {bounded(item.display_name, 60) || bounded(item.system_type, 40) || "חיבור"} —
              הבדיקה האחרונה שתועדה נכשלה.
            </span>
          </p>
          <SourceCite name="GET /api/agents/system-map" query={flowQuery} />
        </>
      ),
    })),
    ...staleIntegrations.map((item) => ({
      cls: "rounded-lg border border-amber-100 bg-amber-50/50 p-2 space-y-1",
      node: (
        <>
          <p className="flex flex-wrap items-center gap-2 text-sm text-gray-700">
            <FlowStateChip state="stale" />
            <span>
              בדיקת החיבור האחרונה אינה מעודכנת:{" "}
              {bounded(item.display_name, 60) || bounded(item.system_type, 40) || "חיבור"}
            </span>
          </p>
          <SourceCite name="GET /api/agents/system-map" query={flowQuery} />
        </>
      ),
    })),
    crisisActive && canManage && {
      cls: "rounded-lg border border-red-100 bg-red-50/50 p-2 space-y-1",
      node: (
        <>
          <p className="text-sm text-red-700">מצב חירום פעיל — פעולות הכתיבה אמורות להיות חסומות ברמת המערכת.</p>
          <SourceCite name="GET /api/agents/crisis-mode" query={crisisQuery} />
        </>
      ),
    },
    ...unclearSources.map((source) => ({
      cls: "flex flex-wrap items-center gap-2 text-xs text-gray-600",
      node: (
        <>
          <QueryTruthChip query={source.query} />
          <span>
            מקור <span dir="ltr" className="font-mono">{source.name}</span> — {source.note}
          </span>
        </>
      ),
    })),
  ];

  return (
    <section
      aria-label={`מיקוד עובד — ${node.name}`}
      data-testid="system-map-focus-worker"
      className="rounded-xl border border-teal-200 bg-white shadow-sm p-4 space-y-3"
    >
      <div className="space-y-1">
        <h3 className="text-base font-semibold text-gray-800">{node.name}</h3>
        <p className="flex flex-wrap items-center gap-2 text-xs text-gray-500">
          {node.role_title && <span>{node.role_title}</span>}
          <ReadinessBadge readiness={readiness} />
          <Link className="text-blue-600 hover:underline" to={`/agents/${node.agent_id}`}>
            לעמוד העובד המלא
          </Link>
        </p>
      </div>

      <div data-testid="system-map-focus-worker-position" className="space-y-1.5">
        <h4 className="text-sm font-semibold text-gray-700">מיקום בזרימה</h4>
        {!live ? (
          <p className="text-sm text-gray-500">אין נתוני סטטוס חיים מהמקור — המיקום בזרימה אינו ידוע; לא מוצג ניחוש.</p>
        ) : currentWork ? (
          <>
            <p
              data-testid="system-map-focus-worker-current-work"
              data-started-at={currentWork.started_at || ""}
              className="flex flex-wrap items-center gap-2 text-sm text-gray-700"
            >
              <span>מטפל כעת ב־</span>
              <TicketLink
                ticketId={currentWork.ticket_id}
                ticketNumber={currentWork.ticket_number}
                label={currentWork.ticket_number != null
                  ? `קריאה #${currentWork.ticket_number}${currentWork.ticket_title ? ` · ${bounded(currentWork.ticket_title, 60)}` : ""}`
                  : "לכרטיס"}
              />
              <RunLink runId={currentWork.run_id} />
              <SourceInstant at={currentWork.started_at} label="התחלה שתועדה:" />
            </p>
            <div data-testid="system-map-focus-worker-run-evidence" className="space-y-1">
              {newestRunTruth ? (
                <p
                  data-testid="system-map-focus-worker-newest-run-event"
                  data-at={newestRunEvent.at}
                  className="flex flex-wrap items-center gap-1.5 text-xs text-gray-600"
                >
                  <FlowStateChip state={newestRunTruth.state} />
                  <span>האירוע האחרון שתועד לריצה זו: {bounded(newestRunEvent.label, 100) || newestRunEvent.kind}</span>
                  {typeof newestRunEvent.age_minutes === "number" && (
                    <span className="text-gray-400">לפני {newestRunEvent.age_minutes} דק׳</span>
                  )}
                  <SourceInstant at={newestRunEvent.at} />
                </p>
              ) : (
                <p className="text-xs text-gray-500">
                  {flow
                    ? "לא תועדו אירועי התחלה/סיום לריצה זו בחלון הסריקה."
                    : "מקור הזרימה לא נטען — מהלך הריצה אינו מוצג."}
                </p>
              )}
              {newestRunTruth && !newestRunTruth.looksLive && (
                <p className="text-xs text-gray-500">
                  הראיה החדשה ביותר אינה מסומנת מעודכנת בשרת — המצב כפי שתועד, לא כעת.
                </p>
              )}
            </div>
          </>
        ) : (
          <p className="text-sm text-gray-700">אינו מטפל כרגע בקריאה לפי המקור החי.</p>
        )}
        <SourceCite name="GET /api/agents/workforce-overview" query={workforceQuery} />
      </div>

      <div data-testid="system-map-focus-worker-blocked" className="space-y-1.5">
        <h4 className="text-sm font-semibold text-gray-700">חסימות והמתנות</h4>
        <FocusBlockList items={blockItems} blockTestId="system-map-focus-worker-block" />
        {absenceClaimAllowed && (
          <p className="text-xs text-gray-500">אין רשומת המתנה או חסימה מתועדת לעובד זה במקורות שנבדקו.</p>
        )}
      </div>

      <div data-testid="system-map-focus-worker-evidence" className="space-y-2">
        <h4 className="text-sm font-semibold text-gray-700">המסלול והחיבורים שתועדו</h4>
        {shownRunEvents.length > 0 && (
          <div className="space-y-1">
            <h5 className="text-xs font-medium text-gray-600">אירועי הריצה הנוכחית</h5>
            <ol className="space-y-1">
              {shownRunEvents.map((event, index) => (
                <FocusEventRow
                  key={`${event.source}-${event.at}-${index}`}
                  event={event}
                  truth={flowEventTruth(event, sourceStates.get(event.source))}
                  testId="system-map-focus-worker-run-event"
                />
              ))}
            </ol>
            {runEvents.length > shownRunEvents.length && (
              <p className="text-[11px] text-gray-400">
                מוצגים {shownRunEvents.length} האירועים החדשים ביותר מתוך {runEvents.length} שנשמרו לריצה זו בחלון הזרימה.
              </p>
            )}
          </div>
        )}
        <div className="space-y-1" data-testid="system-map-focus-worker-connections">
          <h5 className="text-xs font-medium text-gray-600">ראיות בדיקות חיבור מהזרימה</h5>
          {shownIntegrations.length === 0 ? (
            <p className="text-xs text-gray-500">
              {flow
                ? "לא תועדה בדיקת חיבור לעובד זה בחלון הסריקה."
                : "מקור הזרימה לא נטען — ראיות החיבור אינן מוצגות."}
            </p>
          ) : (
            <ul className="space-y-1">
              {shownIntegrations.map((item) => (
                <li
                  key={item.connection_id}
                  data-state={item.state}
                  data-last-tested-at={item.last_tested_at || ""}
                  data-testid="system-map-focus-worker-connection"
                  className="flex flex-wrap items-center gap-2 text-xs text-gray-600"
                >
                  <FlowStateChip state={item.state} />
                  <span>{bounded(item.display_name, 60) || bounded(item.system_type, 40) || "חיבור"}</span>
                  {item.last_test_status && (
                    <span dir="ltr" className="font-mono text-[10px] text-gray-400">
                      {bounded(item.last_test_status, 40)}
                    </span>
                  )}
                  {typeof item.age_minutes === "number" && (
                    <span className="text-[11px] text-gray-400">לפני {item.age_minutes} דק׳</span>
                  )}
                  <SourceInstant at={item.last_tested_at} label="נבדק:" />
                </li>
              ))}
            </ul>
          )}
          {integrations.length > shownIntegrations.length && (
            <p className="text-[11px] text-gray-400">
              מוצגות {shownIntegrations.length} בדיקות החיבור האחרונות מתוך {integrations.length} שנשמרו בחלון הזרימה.
            </p>
          )}
        </div>
      </div>

      <div data-testid="system-map-focus-worker-sources" className="space-y-1">
        <h4 className="text-sm font-semibold text-gray-700">מקורות הראיות לעובד זה</h4>
        <FocusSourceRows rows={familyRows} testIdPrefix="system-map-focus-worker-source" />
        <p className="text-[11px] text-gray-400">
          המיקום והראיות נגזרים מהזרימה החיה (<span dir="ltr" className="font-mono">GET /api/agents/system-map</span>)
          ומהמקורות המצוינים; אין כאן המצאה של מצב.
        </p>
      </div>
    </section>
  );
}

function FocusSection({
  flow,
  query,
  flowFrozen,
  flowOffline,
  approvals,
  approvalsQuery,
  attention,
  workforceQuery,
  agentsQuery,
  crisisQuery,
  employees,
  employeeNodes,
  crisisActive,
  canManage,
}) {
  const [ticketId, setTicketId] = useState("");
  const [workerId, setWorkerId] = useState("");

  const ticketOptions = useMemo(
    () => buildTicketFocusOptions({ flow, approvals, attention, employees }),
    [flow, approvals, attention, employees]
  );
  const workerOptions = useMemo(
    () => buildWorkerFocusOptions(employeeNodes, employees),
    [employeeNodes, employees]
  );
  const shownTicketOptions = withSelectedOption(ticketOptions, ticketId, FOCUS_LIST_CAP);
  const shownWorkerOptions = withSelectedOption(workerOptions, workerId, FOCUS_LIST_CAP);
  const sourceStates = useMemo(
    () => new Map(((flow && flow.sources) || []).map((source) => [source.key, source.state])),
    [flow]
  );

  const flowPending = !flow && !!query?.isLoading;
  const selectedTicket = ticketOptions.find((option) => option.id === ticketId) || null;
  const selectedWorker = employeeNodes.find((node) => node.agent_id === workerId) || null;
  const selectedWorkforceEmployee = selectedWorker
    ? employees.find((employee) => employee.agent_id === selectedWorker.agent_id) || null
    : null;

  return (
    <section aria-label="מיקוד תפעולי" data-testid="system-map-focus" className="space-y-3">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <h2 className="text-lg font-semibold text-gray-800">מיקוד תפעולי — מיקום קריאה או עובד בזרימה</h2>
        <p className="text-[11px] text-gray-400">
          בחירה מתוך מה שתועד בזרימה האמיתית ובמקורות שנטענו בלבד. לקריאה בלבד — אין כאן שינוי מצב.
        </p>
      </div>

      {flowFrozen && (
        <p data-testid="system-map-focus-frozen" className="text-xs text-amber-700">
          {flowOffline
            ? "הדפדפן מנותק מהרשת — הזרימה הוחזקה מהטעינה האחרונה והנתונים כאן אינם חיים; עם חזרת החיבור כל המקורות ירועננו אוטומטית."
            : "רענון הזרימה נכשל — הנתונים כאן אינם חיים עד לרענון מוצלח. לחצו על \"רענן\" בראש העמוד כדי לנסות שוב."}
        </p>
      )}

      {/* A truncated backend window is a hard bound on every focus trail below. Saying so here keeps
          an absent event from reading as an event that never happened. */}
      {flow && flow.truncated && (
        <p data-testid="system-map-focus-window-bound" className="text-xs text-amber-700">
          חלון הזרימה נחתך (עד {flow.event_cap} אירועים לכל מקור ועד {flow.event_cap} בסך הכול) — המיקוד והמסלולים
          מציגים רק את האירועים שנשמרו בחלון; אירועים נוספים עשויים להימצא מחוץ לו ואינם נטענים כאן. מקור:{" "}
          <span dir="ltr" className="font-mono">GET /api/agents/system-map</span>
        </p>
      )}

      <div className="grid gap-3 md:grid-cols-2">
        <div className="space-y-1">
          <label htmlFor="system-map-focus-ticket" className="block text-sm font-medium text-gray-700">
            קריאה למיקוד
          </label>
          <select
            id="system-map-focus-ticket"
            data-testid="system-map-focus-ticket-select"
            className="w-full rounded-lg border border-gray-300 bg-white px-2 py-1.5 text-sm text-gray-800"
            value={selectedTicket ? ticketId : ""}
            onChange={(event) => setTicketId(event.target.value)}
          >
            <option value="">בחרו קריאה מהזרימה</option>
            {shownTicketOptions.map((option) => (
              <option key={option.id} value={option.id}>{ticketFocusLabel(option)}</option>
            ))}
          </select>
          {ticketOptions.length > FOCUS_LIST_CAP && (
            <p className="text-[10px] text-gray-400">
              מוצגות {FOCUS_LIST_CAP} האפשרויות הראשונות — קריאות שממתינות לאישור או לטיפול מוצגות תחילה —
              מתוך {ticketOptions.length} בזרימה ובמקורות.
            </p>
          )}
          {ticketOptions.length === 0 && (
            <p className="text-[10px] text-gray-400">
              {flowPending
                ? "טוען את הזרימה…"
                : flow
                  ? "אין קריאות מתועדות בזרימה ובמקורות שנטענו."
                  : "מקור הזרימה לא נטען — בחירת קריאה מהזרימה אינה זמינה."}
            </p>
          )}
        </div>
        <div className="space-y-1">
          <label htmlFor="system-map-focus-worker" className="block text-sm font-medium text-gray-700">
            עובד למיקוד
          </label>
          <select
            id="system-map-focus-worker"
            data-testid="system-map-focus-worker-select"
            className="w-full rounded-lg border border-gray-300 bg-white px-2 py-1.5 text-sm text-gray-800"
            value={selectedWorker ? workerId : ""}
            onChange={(event) => setWorkerId(event.target.value)}
          >
            <option value="">בחרו עובד</option>
            {shownWorkerOptions.map((option) => (
              <option key={option.id} value={option.id}>{option.name}</option>
            ))}
          </select>
          {workerOptions.length > FOCUS_LIST_CAP && (
            <p className="text-[10px] text-gray-400">
              מוצגים {FOCUS_LIST_CAP} העובדים בעלי פעילות או חסימה רלוונטית ביותר מתוך {workerOptions.length} ברשומה.
            </p>
          )}
          {workerOptions.length === 0 && (
            <p className="text-[10px] text-gray-400">אין עובדים להצגה ברשומה הנוכחית.</p>
          )}
        </div>
      </div>

      {ticketId && !selectedTicket && (
        <p data-testid="system-map-focus-ticket-gone" className="text-xs text-gray-500">
          הקריאה שנבחרה אינה בזרימה או במקורות הנוכחיים — בחרו קריאה מחדש.
        </p>
      )}
      {selectedTicket && (
        <TicketFocusPanel
          ticket={selectedTicket}
          flow={flow}
          sourceStates={sourceStates}
          approvals={approvals}
          approvalsQuery={approvalsQuery}
          workforceQuery={workforceQuery}
          attention={attention}
        />
      )}
      {workerId && !selectedWorker && (
        <p data-testid="system-map-focus-worker-gone" className="text-xs text-gray-500">
          העובד שנבחר אינו ברשומה הנוכחית — בחרו עובד מחדש.
        </p>
      )}
      {selectedWorker && (
        <WorkerFocusPanel
          node={selectedWorker}
          flow={flow}
          flowQuery={query}
          sourceStates={sourceStates}
          workforceEmployee={selectedWorkforceEmployee}
          workforceQuery={workforceQuery}
          agentsQuery={agentsQuery}
          approvals={approvals}
          approvalsQuery={approvalsQuery}
          attention={attention}
          crisisActive={crisisActive}
          crisisQuery={crisisQuery}
          canManage={canManage}
        />
      )}
    </section>
  );
}

function StatusChip({ status }) {
  const meta = statusChipMeta(status);
  return (
    <span className={`live-status-chip ${CHIP_CLS} ${meta.cls}`}>
      {meta.label}
    </span>
  );
}

function ReadinessBadge({ readiness }) {
  const meta = statusChipMeta(readiness.key);
  return (
    <span className={`live-status-chip ${CHIP_CLS} ${meta.cls}`}>
      {readiness.label}
    </span>
  );
}

function DimensionChip({ dimension, children }) {
  const meta = DIMENSION_META[dimension];
  return (
    <span className={`${CHIP_CLS} ${meta.cls}`}>
      <span className="font-medium">{meta.label}:</span> {children}
    </span>
  );
}

function DimensionCard({ dimension, title, children }) {
  const meta = DIMENSION_META[dimension];
  return (
    <section className={`rounded-xl border p-3 space-y-2 ${meta.cls}`} aria-label={title}>
      <h4 className="text-sm font-semibold">{title}</h4>
      <div className="space-y-1.5 text-gray-700">{children}</div>
    </section>
  );
}

function Row({ label, children }) {
  return (
    <div className="flex flex-wrap items-start justify-between gap-x-4 gap-y-1 border-b border-gray-100 last:border-0 py-1.5">
      <span className="text-sm text-gray-500">{label}</span>
      <span className="text-sm text-gray-800">{children}</span>
    </div>
  );
}

const UnknownValue = () => <span className="text-gray-400">לא ידוע</span>;
const FailNote = ({ children = "המקור לא נטען — לא מוצג ניחוש" }) => (
  <p className="text-xs text-gray-500">{children}</p>
);

function SourceLine({ sources }) {
  return (
    <p className="text-[11px] text-gray-400">
      מקורות השלב:{" "}
      {sources.map(({ name, query }, index) => (
        <span key={name}>
          {index > 0 && " · "}
          <span dir="ltr" className="font-mono">{name}</span>
          {sourceSuffix(query)}
        </span>
      ))}
    </p>
  );
}

const RunLink = ({ runId, label = "לריצה" }) =>
  runId ? (
    <Link className="text-blue-600 hover:underline" to={`/agent-runs/${runId}`}>
      {label}
    </Link>
  ) : null;

const TicketLink = ({ ticketId, ticketNumber, label }) =>
  ticketId ? (
    <Link className="text-blue-600 hover:underline" to={`/tickets/${ticketId}`}>
      {label || (ticketNumber ? `קריאה #${ticketNumber}` : "לכרטיס")}
    </Link>
  ) : ticketNumber ? (
    <span>קריאה #{ticketNumber}</span>
  ) : null;

function StageDetail({ stageId, ctx }) {
  if (stageId === "intake") {
    const { openTickets, openTicketsQuery, aiSettings, aiSettingsQuery, isAdmin } = ctx;
    return (
      <div className="space-y-2">
        <p className="text-sm text-gray-600">{STAGES[0].hint}</p>
        <Row label="קריאות פתוחות כרגע">
          {Array.isArray(openTickets) ? (
            openTickets.length === 0 ? (
              "אין קריאות פתוחות"
            ) : (
              `${openTickets.length}${openTickets.length >= OPEN_TICKET_CAP ? `+ (מוצג עד ${OPEN_TICKET_CAP})` : ""}`
            )
          ) : sourceFailed(openTicketsQuery) ? (
            <UnknownValue />
          ) : (
            "טוען…"
          )}
        </Row>
        {isAdmin && (
          <>
            <Row label="פיילוט AI Technician">
              {aiSettings
                ? aiSettings.ai_technician_pilot_enabled
                  ? "מופעל"
                  : "כבוי"
                : sourceFailed(aiSettingsQuery)
                  ? <UnknownValue />
                  : "טוען…"}
            </Row>
            <Row label="טריגר אוטומטי">
              {aiSettings
                ? aiSettings.ai_auto_trigger_enabled
                  ? "מופעל"
                  : "כבוי"
                : sourceFailed(aiSettingsQuery)
                  ? <UnknownValue />
                  : "טוען…"}
            </Row>
          </>
        )}
        <p className="text-xs text-gray-500">
          אין במפה כדי להפעיל טיפול — הפעלה נעשית במסך הקריאה או דרך טריגר מוגדר בלבד.{" "}
          <Link className="text-blue-600 hover:underline" to="/tickets">למסך הקריאות</Link>
        </p>
        <SourceLine sources={[
          { name: "GET /api/tickets/?open_only", query: openTicketsQuery },
          ...(isAdmin ? [{ name: "GET /api/settings/ai", query: aiSettingsQuery }] : []),
        ]} />
      </div>
    );
  }

  if (stageId === "routing") {
    const { workforce, workforceQuery, employees, totals, attention, handlingNow } = ctx;
    const handling = employees.filter((employee) => employee.current_work).slice(0, LIST_CAP);
    return (
      <div className="space-y-2">
        <p className="text-sm text-gray-600">{STAGES[1].hint}</p>
        {!workforce ? (
          sourceFailed(workforceQuery) ? <FailNote /> : <p className="text-sm text-gray-400">טוען…</p>
        ) : (
          <>
            <Row label="עובדים מטפלים כעת">
              {handlingNow === 0 ? "אין עבודות פעילות כעת" : `${handlingNow}`}
            </Row>
            {handling.length > 0 && (
              <ul className="space-y-1">
                {handling.map((employee) => (
                  <li key={employee.agent_id} className="text-sm text-gray-700 flex flex-wrap items-center gap-2">
                    <span className="font-medium">{employee.name}</span>
                    {employee.current_work?.ticket_id || employee.current_work?.ticket_number ? (
                      <TicketLink
                        ticketId={employee.current_work.ticket_id}
                        ticketNumber={employee.current_work.ticket_number}
                        label={`קריאה #${employee.current_work.ticket_number ?? ""}${employee.current_work.ticket_title ? ` · ${bounded(employee.current_work.ticket_title, 60)}` : ""}`}
                      />
                    ) : (
                      <span className="text-gray-500">משימה מתוך שיחה</span>
                    )}
                    <RunLink runId={employee.current_work?.run_id} />
                  </li>
                ))}
              </ul>
            )}
            <Row label="ממתינים למפעיל">
              {(totals?.awaiting_operator || 0) === 0 ? "אין המתנות למפעיל" : `${totals.awaiting_operator}`}
            </Row>
            {attention.length > 0 && (
              <ul className="space-y-1">
                {attention.slice(0, LIST_CAP).map((item) => (
                  <li key={`${item.kind}-${item.run_id}`} className="text-sm text-gray-700 flex flex-wrap items-center gap-2">
                    <span className="text-amber-700">{item.label}</span>
                    <span className="text-gray-500">
                      {item.agent_name}
                      {item.ticket_number ? ` · קריאה #${item.ticket_number}` : ""}
                    </span>
                    <RunLink runId={item.run_id} />
                  </li>
                ))}
              </ul>
            )}
          </>
        )}
        <p className="text-xs text-gray-500">
          כל פריט הממתין לאדם מופיע גם ב
          <Link className="text-blue-600 hover:underline" to="/attention-inbox">תיבת תשומת הלב התפעולית</Link>.
        </p>
        <SourceLine sources={[{ name: "GET /api/agents/workforce-overview", query: workforceQuery }]} />
      </div>
    );
  }

  if (stageId === "conversation") {
    const { conversations, conversationsQuery } = ctx;
    const recent = Array.isArray(conversations) ? conversations.slice(0, LIST_CAP) : null;
    const openCount = Array.isArray(conversations)
      ? conversations.filter((conversation) => conversation.status !== "closed").length
      : null;
    return (
      <div className="space-y-2">
        <p className="text-sm text-gray-600">{STAGES[2].hint}</p>
        {recent === null ? (
          sourceFailed(conversationsQuery) ? <FailNote /> : <p className="text-sm text-gray-400">טוען…</p>
        ) : (
          <>
            <Row label="שיחות פתוחות מתוך האחרונות">
              {conversations.length === 0 ? "אין שיחות אחרונות" : `${openCount} מתוך ${conversations.length}`}
            </Row>
            {recent.length > 0 && (
              <ul className="space-y-1">
                {recent.map((conversation) => (
                  <li key={conversation.id} className="text-sm text-gray-700 flex flex-wrap items-center gap-2">
                    <span className="text-gray-600">{conversationStatusLabel(conversation.status)}</span>
                    <Link className="text-blue-600 hover:underline" to={`/conversations/${conversation.id}`}>
                      לשיחה
                    </Link>
                    <TicketLink ticketId={conversation.ticket_id} label="לכרטיס" />
                    {fmtDateTime(conversation.updated_at) && (
                      <span className="text-gray-400 text-xs">עודכנה {fmtDateTime(conversation.updated_at)}</span>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </>
        )}
        <p className="text-xs text-gray-500">
          תצוגת השיחה המלאה וכתיבת הודעות נעשות במסך השיחה — המפה מציגה מצב בלבד.{" "}
          <Link className="text-blue-600 hover:underline" to="/conversations">למסך השיחות</Link>
        </p>
        <SourceLine sources={[{ name: "GET /api/conversations/", query: conversationsQuery }]} />
      </div>
    );
  }

  if (stageId === "skills") {
    const { workforce, workforceQuery, knowledge, knowledgeQuery, employees, isAdmin } = ctx;
    const declaredCount = employees.filter(
      (employee) => (employee.specialist_readiness?.declared_specialties || []).length > 0
    ).length;
    const knowledgeKnown = Array.isArray(knowledge);
    return (
      <div className="space-y-2">
        <p className="text-sm text-gray-600">{STAGES[3].hint}</p>
        <Row label="עובדים עם תחומים מוצהרים">
          {workforce ? (declaredCount === 0 ? "טרם הוצהרו תחומים" : declaredCount) : <UnknownValue />}
        </Row>
        <Row label="בסיס ידע מאושר">
          {knowledgeKnown ? (knowledge.length > 0 ? "קיים" : "ריק") : <UnknownValue />}
        </Row>
        <p className="text-xs text-gray-500">
          פירוט היכולת של כל עובד — כולל מדידה מול הצהרה — מוצג בתצוגת העומק של העובד למטה.{" "}
          <Link className="text-blue-600 hover:underline" to="/knowledge">לבסיס הידע</Link>
          {isAdmin && (
            <>
              {" · "}
              <Link className="text-blue-600 hover:underline" to="/skills">למיומנויות ולמדידות</Link>
            </>
          )}
        </p>
        <SourceLine sources={[
          { name: "GET /api/agents/workforce-overview", query: workforceQuery },
          { name: "GET /api/knowledge/", query: knowledgeQuery },
        ]} />
      </div>
    );
  }

  if (stageId === "connections") {
    const { connectionsQuery, systemConnections, configuredCount } = ctx;
    return (
      <div className="space-y-2">
        <p className="text-sm text-gray-600">{STAGES[4].hint}</p>
        {!systemConnections ? (
          sourceFailed(connectionsQuery) ? <FailNote /> : <p className="text-sm text-gray-400">טוען…</p>
        ) : (
          <>
            <Row label="מערכות מוכנות">
              {configuredCount}/{systemConnections.length}
            </Row>
            <ul className="space-y-1">
              {systemConnections.map((system) => (
                <li key={system.key} className="flex flex-wrap items-center gap-2 text-sm">
                  <StatusChip status={system.configured ? "active" : "requires_configuration"} />
                  <span className="text-gray-800">{system.label}</span>
                  {system.note && <span className="text-xs text-gray-400">{system.note}</span>}
                </li>
              ))}
            </ul>
          </>
        )}
        <p className="text-xs text-gray-500">
          חיבור מוגדר אינו הרשאה ואינו ביצוע: שימוש במערכת חיצונית עובר הרשאות, אישור ובקרה.{" "}
          <Link className="text-blue-600 hover:underline" to="/connections">למסך החיבורים</Link>
        </p>
        <SourceLine sources={[{ name: "GET /api/connections/health", query: connectionsQuery }]} />
      </div>
    );
  }

  if (stageId === "approval") {
    const { approvals, approvalsQuery } = ctx;
    return (
      <div className="space-y-2">
        <p className="text-sm text-gray-600">{STAGES[5].hint}</p>
        {!approvals ? (
          sourceFailed(approvalsQuery) ? <FailNote /> : <p className="text-sm text-gray-400">טוען…</p>
        ) : (
          <>
            <Row label="ממתינים לאישור">
              {approvals.pending_count === 0 ? "אין אישורים ממתינים" : `${approvals.pending_count}`}
            </Row>
            <Row label="עובדים שממתינים לאישור">
              {approvals.agents_waiting_count === 0 ? "אין" : `${approvals.agents_waiting_count}`}
            </Row>
            {(approvals.pending || []).length > 0 && (
              <ul className="space-y-1.5">
                {approvals.pending.slice(0, LIST_CAP).map((item) => (
                  <li key={item.run_id} className="rounded-lg border border-blue-100 bg-blue-50/50 p-2 space-y-1">
                    <div className="flex flex-wrap items-center gap-2 text-sm">
                      <span className="font-medium text-gray-800">{item.agent_name}</span>
                      <TicketLink ticketId={item.ticket_id} ticketNumber={item.ticket_number} />
                      <RunLink runId={item.run_id} />
                    </div>
                    {item.pending_action && (
                      <p className="text-xs text-gray-600">
                        {bounded(item.pending_action.description, 160)}{" "}
                        <span className="text-gray-400">· {riskLabel(item.pending_action.risk_level)}</span>
                      </p>
                    )}
                    {fmtDateTime(item.wait_started_at) && (
                      <p className="text-[11px] text-gray-400">ממתין מרגע ההצעה: {fmtDateTime(item.wait_started_at)}</p>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </>
        )}
        <p className="text-xs text-gray-500">
          אין במפה כפתור אישור או דחייה — ההחלטה מתבצעת במסך הריצה, עם ההקשר המלא.{" "}
          <Link className="text-blue-600 hover:underline" to="/agent-runs">למסך הריצות</Link>
        </p>
        <SourceLine sources={[{ name: "GET /api/agent-runs/pending-approvals", query: approvalsQuery }]} />
      </div>
    );
  }

  if (stageId === "execution") {
    const { connections, connectionsQuery, crisis, crisisQuery, canManage, aiSettings, aiSettingsQuery, isAdmin, totals } = ctx;
    const tools = Array.isArray(connections?.tool_registry) ? connections.tool_registry : null;
    const teamviewer = connections?.teamviewer || null;
    return (
      <div className="space-y-3">
        <p className="text-sm text-gray-600">{STAGES[6].hint}</p>
        {canManage && (
          <Row label="מצב חירום">
            {crisis?.active === true
              ? "פעיל — כל פעולות הכתיבה אמורות להיות חסומות"
              : crisis
                ? "לא פעיל"
                : sourceFailed(crisisQuery)
                  ? <UnknownValue />
                  : "טוען…"}
          </Row>
        )}
        {isAdmin && (
          <Row label="פיילוט AI Technician">
            {aiSettings
              ? aiSettings.ai_technician_pilot_enabled
                ? "מופעל"
                : "כבוי — ביצוע חסום"
              : sourceFailed(aiSettingsQuery)
                ? <UnknownValue />
                : "טוען…"}
          </Row>
        )}
        <Row label="ריצות בפעולה כרגע">
          {totals ? totals.active_runs : <UnknownValue />}
        </Row>

        <div className="grid gap-2 md:grid-cols-3">
          <div className="rounded-lg border border-gray-200 p-2.5 space-y-1.5">
            <h4 className="text-sm font-semibold text-gray-800">כלים ו-API</h4>
            {tools === null ? (
              sourceFailed(connectionsQuery) ? <FailNote /> : <p className="text-xs text-gray-400">טוען…</p>
            ) : tools.length === 0 ? (
              <p className="text-xs text-gray-500">אין כלי ביצוע רשומים</p>
            ) : (
              <ul className="space-y-1.5">
                {tools.map((tool) => (
                  <li key={tool.id} className="text-xs text-gray-600 space-y-0.5">
                    <div className="flex flex-wrap items-center gap-1.5">
                      <StatusChip status={tool.status === "requires_connected_node" ? "requires_configuration" : "active"} />
                      <span dir="ltr" className="font-mono text-gray-800">{tool.label}</span>
                    </div>
                    <p>
                      {riskGradeLabel(tool.risk_level)}
                      {tool.approval_required ? " · דורש אישור" : ""}
                      {tool.strong_approval_required ? " · אישור מוגבר" : ""}
                    </p>
                    {tool.status === "requires_connected_node" && tool.readiness_dependency && (
                      <p className="text-gray-400">תלות מוכנות: {tool.readiness_dependency}</p>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </div>
          <div className="rounded-lg border border-gray-200 p-2.5 space-y-1.5">
            <h4 className="text-sm font-semibold text-gray-800">דפדפן (Browser Worker)</h4>
            <p className="text-xs text-gray-600">
              מסלול הדפדפן מיועד לפעולות שדורשות ממשק משתמש בדפדפן במקום API.
            </p>
            <StatusChip status="no_evidence" />
            <p className="text-xs text-gray-500">אין מקור מצב חי למסלול זה בגרסה הנוכחית — לא מוצג ניחוש.</p>
          </div>
          <div className="rounded-lg border border-gray-200 p-2.5 space-y-1.5">
            <h4 className="text-sm font-semibold text-gray-800">מרחוק (Remote)</h4>
            {teamviewer === null ? (
              sourceFailed(connectionsQuery) ? <FailNote /> : <p className="text-xs text-gray-400">טוען…</p>
            ) : (
              <>
                <StatusChip status={teamviewer.configured ? "active" : "requires_configuration"} />
                <p className="text-xs text-gray-600">
                  {teamviewer.configured ? "TeamViewer מוגדר" : "TeamViewer לא מוגדר"}
                </p>
              </>
            )}
            <p className="text-xs text-gray-500">חיבורי שליטה מרחוק נפתחים ביוזמת מפעיל בלבד.</p>
          </div>
        </div>
        <p className="text-xs text-gray-500">
          כל ביצוע עובר בנתיב הפעולה הקנוני: תכנון, מדיניות וסיכון, אישור כשנדרש, הרשאה מוגבלת בזמן, כלי מבוקר ובקרת תוצאה.{" "}
          <Link className="text-blue-600 hover:underline" to="/connections">למצב החיבורים</Link>
        </p>
        <SourceLine sources={[
          { name: "GET /api/connections/health", query: connectionsQuery },
          ...(canManage ? [{ name: "GET /api/agents/crisis-mode", query: crisisQuery }] : []),
          ...(isAdmin ? [{ name: "GET /api/settings/ai", query: aiSettingsQuery }] : []),
        ]} />
      </div>
    );
  }

  if (stageId === "verification") {
    const { workforce, workforceQuery, failures, failuresQuery, employees } = ctx;
    const totals = workforce?.totals || null;
    const evidence = workforce?.outcome_evidence || null;
    const verified = workforce?.recent_verified_outcomes || [];
    const failureRows = Array.isArray(failures?.failures) ? failures.failures.slice(0, LIST_CAP) : null;
    return (
      <div className="space-y-2">
        <p className="text-sm text-gray-600">{STAGES[7].hint}</p>
        {!workforce ? (
          sourceFailed(workforceQuery) ? <FailNote /> : <p className="text-sm text-gray-400">טוען…</p>
        ) : (
          <>
            <Row label={`עבודות מאומתות (חלון ${workforce.window_days ?? "?"} ימים)`}>
              {totals.verified_successes}
            </Row>
            <Row label="הסתיימו בלי אימות טכנאי">{totals.unverified_completions}</Row>
            <Row label="כשלונות בחלון">{totals.failures}</Row>
            <Row label="עובדים עם תיעוד תוצאות">
              {employees.filter((employee) => employee.specialist_readiness?.has_outcome_evidence).length} מתוך {employees.length}
            </Row>
          </>
        )}
        {verified.length > 0 && (
          <ul className="space-y-1">
            {verified.slice(0, LIST_CAP).map((outcome) => (
              <li key={outcome.run_id} className="text-sm text-gray-700 flex flex-wrap items-center gap-2">
                <span className="text-green-700">אומת</span>
                <span className="font-medium">{outcome.agent_name}</span>
                <TicketLink ticketId={outcome.ticket_id} ticketNumber={outcome.ticket_number} />
                <RunLink runId={outcome.run_id} />
              </li>
            ))}
          </ul>
        )}
        <div className="pt-1 space-y-1">
          <h4 className="text-sm font-semibold text-gray-700">כשלונות ב־24 שעות</h4>
          {failureRows === null ? (
            sourceFailed(failuresQuery) ? <FailNote /> : <p className="text-xs text-gray-400">טוען…</p>
          ) : failureRows.length === 0 ? (
            <p className="text-xs text-gray-500">אין כשלונות שתועדו בחלון</p>
          ) : (
            <ul className="space-y-1">
              {failureRows.map((failure) => (
                <li key={failure.run_id} className="text-sm text-gray-700 flex flex-wrap items-center gap-2">
                  <span className="text-red-700">{failure.agent_name}</span>
                  {bounded(failure.summary, 120) && <span className="text-gray-500">{bounded(failure.summary, 120)}</span>}
                  <TicketLink ticketId={failure.ticket_id} ticketNumber={failure.ticket_number} />
                  <RunLink runId={failure.run_id} />
                </li>
              ))}
            </ul>
          )}
        </div>
        {evidence && (
          <p className="text-[11px] text-gray-400">
            כלל האימות (כפי שהשרת מדווח): <span dir="ltr" className="font-mono">{evidence.basis}</span>
            {evidence.truncated
              ? ` · הסריקה הגיעה למכסת ${evidence.cap} — ייתכנו עבודות ותיקות שלא נספרו`
              : ` · נסרקו ${evidence.scanned} עבודות`}
            {evidence.unmatched_runs > 0 && ` · ${evidence.unmatched_runs} ריצות ללא התאמת קריאה לא נכללו`}
          </p>
        )}
        <SourceLine sources={[
          { name: "GET /api/agents/workforce-overview", query: workforceQuery },
          { name: "GET /api/agent-runs/recent-failures", query: failuresQuery },
        ]} />
      </div>
    );
  }

  // learning
  const { gaps, gapsQuery, feedback, feedbackQuery, isAdmin, canManage, knowledge, knowledgeQuery } = ctx;
  const knowledgeKnown = Array.isArray(knowledge);
  const executedTotal =
    feedback && feedback.executed_actions && typeof feedback.executed_actions === "object"
      ? Object.values(feedback.executed_actions).reduce((sum, value) => sum + (Number(value) || 0), 0)
      : null;
  return (
    <div className="space-y-2">
      <p className="text-sm text-gray-600">{STAGES[8].hint}</p>
      {isAdmin && (
        <Row label="פערי ידע הממתינים למאמר">
          {Array.isArray(gaps) ? (gaps.length === 0 ? "אין פערים פתוחים" : gaps.length) : <UnknownValue />}
        </Row>
      )}
      <Row label="תיקוני מפעילים שנרשמו">
        {feedback ? feedback.total_edits : <UnknownValue />}
      </Row>
      <Row label="שיעור אימוץ ההצעות">
        {feedback
          ? feedback.adoption_rate == null
            ? <UnknownValue />
            : `${feedback.adoption_rate}%`
          : <UnknownValue />}
      </Row>
      <Row label="פעולות שבוצעו לאחר אישור">
        {feedback ? (executedTotal === null ? <UnknownValue /> : executedTotal) : <UnknownValue />}
      </Row>
      {!feedback && (sourceFailed(feedbackQuery) || feedbackQuery?.isLoading === false) && (
        <FailNote>
          מקור המשוב לא נטען — ייתכן שאין הרשאה לצפות בו או שהפיילוט כבוי; לא מוצג ניחוש.
        </FailNote>
      )}
      <Row label="בסיס ידע מאושר">
        {knowledgeKnown ? (knowledge.length > 0 ? "קיים" : "ריק") : <UnknownValue />}
      </Row>
      <p className="text-xs text-gray-500">
        הפרופיל המקצועי המלא — הצהרה מול מדידה, יעדי למידה ומגבלות — מוצג בתצוגת העומק של העובד.{" "}
        <Link className="text-blue-600 hover:underline" to="/knowledge">לבסיס הידע</Link>
        {canManage && (
          <>
            {" · "}
            <Link className="text-blue-600 hover:underline" to="/feedback">לאיכות ולמשוב</Link>
          </>
        )}
      </p>
      <SourceLine sources={[
        ...(isAdmin ? [{ name: "GET /api/knowledge-gaps/", query: gapsQuery }] : []),
        { name: "GET /api/agent-runs/feedback/stats", query: feedbackQuery },
        { name: "GET /api/knowledge/", query: knowledgeQuery },
      ]} />
    </div>
  );
}

function EmployeeDrillDown({ node, agent, workforceEmployee, entitlementAgent, entitlementMeta, queries, crisisActive, canManage, onClose }) {
  const { workspaceQuery, growthQuery, runsQuery } = queries;
  const workspace = workspaceQuery?.data || null;
  const operatorSummary = workspace ? deriveOperatorSummary(workspace) : null;
  const attentionItems = workspace ? deriveAttentionItems(workspace) : [];
  const readiness = employeeReadinessChip(workforceEmployee || node, { crisisActive });
  const declared = workforceEmployee?.specialist_readiness?.declared_specialties || [];
  const evidence = workforceEmployee?.specialist_readiness?.evidence || null;
  const hasEvidence = workforceEmployee?.specialist_readiness?.has_outcome_evidence === true;
  const successRate = workforceEmployee?.specialist_readiness?.verified_success_rate;
  const workspaceConnections = Array.isArray(workspace?.capabilities?.connections)
    ? workspace.capabilities.connections
    : null;

  return (
    <div role="region" aria-label={`פרטי עובד — ${node.name}`} className="rounded-xl border border-violet-200 bg-white shadow-sm p-4 space-y-4">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-3">
          <EmployeeAvatar
            name={node.name}
            avatarKey={node.avatar_key}
            avatarColor={node.avatar_color}
            status={node.availability_status}
            isEnabled={node.is_enabled}
            size="lg"
            showStatus
          />
          <div className="space-y-1">
            <h3 className="text-lg font-semibold text-gray-800">{node.name}</h3>
            {node.role_title && <p className="text-sm text-gray-500">{node.role_title}</p>}
            <div className="flex flex-wrap items-center gap-1.5">
              <ReadinessBadge readiness={readiness} />
              {operatorSummary && (
                <span className="text-xs text-gray-500">
                  {operatorSummary.label}
                  {operatorSummary.detail ? ` — ${operatorSummary.detail}` : ""}
                </span>
              )}
            </div>
            {operatorSummary?.links?.length > 0 && (
              <p className="flex flex-wrap gap-2 text-xs">
                {operatorSummary.links.map((link) => (
                  <Link key={link.to} className="text-blue-600 hover:underline" to={link.to}>
                    {link.label}
                  </Link>
                ))}
              </p>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Link className="text-sm text-blue-600 hover:underline" to={`/agents/${node.agent_id}`}>
            לעמוד העובד המלא
          </Link>
          <button
            type="button"
            onClick={onClose}
            className="text-sm border border-gray-300 rounded-lg px-3 py-1.5 hover:bg-gray-50"
          >
            סגור
          </button>
        </div>
      </div>

      <div className="grid gap-3 lg:grid-cols-3">
        <DimensionCard dimension="capability" title="יכולת — מה העובד יודע לעשות">
          <p>
            תחומים מוצהרים:{" "}
            {declared.length > 0 ? declared.join(" · ") : <span className="text-gray-400">טרם הוצהרו תחומים</span>}
          </p>
          {hasEvidence ? (
            <p>
              תיעוד תוצאות: קיים
              {typeof successRate === "number" && (
                <>
                  {" · "}הצלחה מאומתת {Math.round(successRate * 100)}%
                  {evidence && ` (${evidence.verified_successes} מתוך ${evidence.terminal_runs} שהסתיימו)`}
                </>
              )}
            </p>
          ) : (
            <p className="text-gray-400">תיעוד תוצאות: אין עדיין עבודות שהסתיימו</p>
          )}
          <p className="text-[11px] text-gray-500">הצהרה אינה מדידה ואינה הרשאה; המדידה המלאה מוצגת בכרטיס המקצועי למטה.</p>
        </DimensionCard>

        <DimensionCard dimension="permission" title="הרשאה — מה מותר לעובד לעשות">
          <p>
            רמת הרשאה:{" "}
            {entitlementAgent?.permission_tier || agent?.permission_tier || <span className="text-gray-400">לא ידוע</span>}
          </p>
          <p>
            רשימת כלים: {permissionText(agent)}
            {entitlementAgent?.allowed_tools === null && " (ללא הגבלת רשימה)"}
          </p>
          {canManage ? (
            entitlementAgent ? (
              <>
                <p>
                  כלים מורשים לפי זכאות:{" "}
                  {entitlementAgent.tools.filter((tool) => tool.entitled).length} מתוך {entitlementAgent.tools.length}
                </p>
                {entitlementAgent.blocked_write_actions.length > 0 && (
                  <p className="text-amber-700">
                    פעולות כתיבה חסומות להיום: {entitlementAgent.blocked_write_actions.length}
                  </p>
                )}
                {entitlementMeta && (
                  <p className="text-[11px] text-gray-500">
                    אכיפת זכאות כלים בפועל: {entitlementMeta.enforcement_currently_enabled ? "פעילה" : "כבויה כרגע — ההרשאות מוצגות כהצהרה בלבד"}
                  </p>
                )}
              </>
            ) : (
              <p className="text-gray-400">פירוט הזכאות לא נטען</p>
            )
          ) : (
            <p className="text-gray-500">
              פירוט זכאות הכלים דורש הרשאת ניהול עובדים דיגיטליים — מוצגת רשומת ההרשאה בלבד. אין הרשאה להציג יותר.
            </p>
          )}
          <p className="text-[11px] text-gray-500">הרשאה אינה ביצוע: כל פעולה חיצונית עוברת אישור ובקרה בנתיב הפעולה הקנוני.</p>
        </DimensionCard>

        <DimensionCard dimension="readiness" title="מוכנות כעת — מה הוא יכול לעשות עכשיו">
          <p>
            מצב: <ReadinessBadge readiness={readiness} />
          </p>
          {workforceEmployee?.live === false ? (
            <p className="text-gray-400">הסטטוס החי לא נטען — לא מוצג ניחוש.</p>
          ) : (
            <>
              <p>
                עבודות פעילות: {workforceEmployee?.workload?.active_runs ?? 0} · ממתין למפעיל:{" "}
                {workforceEmployee?.awaiting_operator ?? 0}
              </p>
              {(workforceEmployee?.awaiting_operator || 0) > 0 && (
                <p className="text-amber-700">קיים פריט שממתין להחלטת מפעיל לפני המשך.</p>
              )}
            </>
          )}
          {crisisActive && <p className="text-red-700">מצב חירום פעיל — פעולות כתיבה חסומות ברמת המערכת.</p>}
          {workspaceConnections === null ? (
            sourceFailed(workspaceQuery) ? (
              <p className="text-gray-400">פירוט החיבורים של העובד לא נטען — לא מוצג ניחוש.</p>
            ) : (
              <p className="text-gray-400">טוען חיבורים…</p>
            )
          ) : workspaceConnections.length === 0 ? (
            <p className="text-gray-400">לא הוגדרו חיבורים לעובד זה.</p>
          ) : (
            <ul className="space-y-0.5">
              {workspaceConnections.slice(0, LIST_CAP).map((connection) => (
                <li key={connection.id} className="text-xs text-gray-700">
                  {connection.display_name || connection.system_type} — {testStatusLabel(connection.last_test_status)}
                  {fmtDateTime(connection.last_tested_at) ? ` (${fmtDateTime(connection.last_tested_at)})` : ""}
                </li>
              ))}
            </ul>
          )}
        </DimensionCard>
      </div>

      {attentionItems.length > 0 && (
        <div className="rounded-lg border border-amber-200 bg-amber-50 p-2.5 space-y-1">
          <h4 className="text-sm font-semibold text-amber-900">דורש תשומת לב</h4>
          <ul className="space-y-1">
            {attentionItems.map((item) => (
              <li key={item.key} className="text-sm text-amber-900 flex flex-wrap items-center gap-2">
                <span>{item.label}</span>
                {item.detail && <span className="text-amber-800/70 text-xs">{item.detail}</span>}
                {(item.links || []).map((link) => (
                  <Link key={link.to} className="text-blue-700 hover:underline text-xs" to={link.to}>
                    {link.label}
                  </Link>
                ))}
              </li>
            ))}
          </ul>
        </div>
      )}

      <EmployeeIdentityCard agent={agent || { name: node.name, role_title: node.role_title, specialties: declared }} growthProfile={growthQuery?.data} agentId={node.agent_id} />

      <ExperienceTimeline runs={runsQuery?.data || []} isLoading={runsQuery?.isLoading} isError={sourceFailed(runsQuery)} />
    </div>
  );
}

export default function SystemMapLivePage() {
  const { user } = useAuth();
  const { can, loading: permsLoading } = useAgentPermissions();
  const canView = can("agents_view");
  const canManage = can("agents_manage");
  const isAdmin = user?.role === "admin" || user?.role === "owner";
  const isOnline = useOnlineStatus();
  const prefersReducedMotion = usePrefersReducedMotion();

  const [activeStage, setActiveStage] = useState("intake");
  const [selectedAgentId, setSelectedAgentId] = useState(null);

  const agentsQuery = useQuery({
    queryKey: ["system-map", "agents"],
    queryFn: () => agentsApi.list(),
    enabled: canView,
    staleTime: 30_000,
    refetchInterval: 30_000,
  });
  const workforceQuery = useQuery({
    queryKey: ["system-map", "workforce"],
    queryFn: () => agentsApi.workforceOverview(),
    enabled: canView,
    staleTime: 30_000,
    refetchInterval: 30_000,
  });
  const openTicketsQuery = useQuery({
    queryKey: ["system-map", "open-tickets"],
    queryFn: () => ticketsApi.list({ open_only: true, limit: OPEN_TICKET_CAP }),
    enabled: canView,
    staleTime: 60_000,
  });
  const conversationsQuery = useQuery({
    queryKey: ["system-map", "conversations"],
    queryFn: () => conversationsApi.list({ limit: 20 }),
    enabled: canView,
    staleTime: 30_000,
    refetchInterval: 60_000,
  });
  const approvalsQuery = useQuery({
    queryKey: ["system-map", "approvals"],
    queryFn: () => agentRunsApi.pendingApprovals(),
    enabled: canView,
    staleTime: 20_000,
    refetchInterval: 30_000,
  });
  const failuresQuery = useQuery({
    queryKey: ["system-map", "recent-failures"],
    queryFn: () => agentRunsApi.recentFailures(),
    enabled: canView,
    staleTime: 60_000,
    refetchInterval: 60_000,
  });
  const connectionsQuery = useQuery({
    queryKey: ["system-map", "connections"],
    queryFn: () => connectionsApi.health(),
    enabled: canView,
    staleTime: 60_000,
  });
  const knowledgeQuery = useQuery({
    queryKey: ["system-map", "knowledge-presence"],
    queryFn: () => knowledgeApi.list({ limit: 1, sort_by: "recent" }),
    enabled: canView,
    staleTime: 300_000,
  });
  const crisisQuery = useQuery({
    queryKey: ["system-map", "crisis"],
    queryFn: () => agentsApi.getCrisisMode(),
    enabled: canView && canManage,
    staleTime: 30_000,
    refetchInterval: 60_000,
  });
  const entitlementsQuery = useQuery({
    queryKey: ["system-map", "entitlements"],
    queryFn: () => agentsApi.entitlementReadiness(),
    enabled: canView && canManage,
    staleTime: 60_000,
  });
  const aiSettingsQuery = useQuery({
    queryKey: ["system-map", "ai-settings"],
    queryFn: () => aiApi.getSettings(),
    enabled: canView && isAdmin,
    staleTime: 120_000,
  });
  const gapsQuery = useQuery({
    queryKey: ["system-map", "knowledge-gaps"],
    queryFn: () => knowledgeGapsApi.listNeedsArticle(),
    enabled: canView && isAdmin,
    staleTime: 120_000,
  });
  // The pilot flag is known to admins; when it is known to be off the stats endpoint answers 403
  // by design, so the query is skipped and the stage reports the flag itself as the reason.
  const pilotFlagKnownOff = isAdmin && aiSettingsQuery.data?.ai_technician_pilot_enabled === false;
  const feedbackQuery = useQuery({
    queryKey: ["system-map", "feedback-stats"],
    queryFn: () => agentRunsApi.feedbackStats(),
    enabled: canView && !pilotFlagKnownOff,
    staleTime: 120_000,
  });
  // The authoritative transition feed (148) — the only driver of the flow motion below.
  const eventFlowQuery = useQuery({
    queryKey: ["system-map", "event-flow"],
    queryFn: () => api.get("/api/agents/system-map").then((response) => response.data),
    enabled: canView,
    staleTime: 30_000,
    refetchInterval: 30_000,
  });

  const workspaceQuery = useQuery({
    queryKey: ["system-map", "employee-workspace", selectedAgentId],
    queryFn: () => agentsApi.workspace(selectedAgentId),
    enabled: canView && !!selectedAgentId,
    staleTime: 30_000,
    refetchInterval: selectedAgentId ? 60_000 : false,
  });
  const growthQuery = useQuery({
    queryKey: ["system-map", "employee-growth", selectedAgentId],
    queryFn: () => agentsApi.growthProfile(selectedAgentId),
    enabled: canView && !!selectedAgentId,
    staleTime: 120_000,
  });
  const runsQuery = useQuery({
    queryKey: ["system-map", "employee-runs", selectedAgentId],
    queryFn: () => agentRunsApi.listAll({ agent_id: selectedAgentId, limit: 50 }),
    enabled: canView && !!selectedAgentId,
    staleTime: 30_000,
  });

  const agentsById = useMemo(() => new Map((agentsQuery.data || []).map((agent) => [agent.id, agent])), [agentsQuery.data]);
  const workforce = workforceQuery.data || null;
  const employees = useMemo(() => workforce?.employees || [], [workforce]);
  const totals = workforce?.totals || null;
  const attention = workforce?.attention || [];
  const connections = connectionsQuery.data || null;
  const crisis = crisisQuery.data || null;
  const aiSettings = aiSettingsQuery.data || null;
  const entitlement = entitlementsQuery.data || null;
  const eventFlow = eventFlowQuery.data?.event_flow || null;
  // A held snapshot is not live: no network, or a refresh that failed while react-query still
  // serves the last successful payload, freezes the motion instead of animating stale data.
  // The two causes stay apart in the disclosures below — offline is not a failed refresh.
  const flowOffline = !isOnline;
  const eventFlowFrozen = flowOffline || isSourceStale(eventFlowQuery);

  const employeeNodes = useMemo(() => {
    if (employees.length > 0) return employees.map((employee) => ({ ...employee, live: true }));
    if (Array.isArray(agentsQuery.data)) {
      return agentsQuery.data.map((agent) => ({
        agent_id: agent.id,
        name: agent.name,
        role_title: agent.role_title,
        avatar_key: agent.avatar_key,
        avatar_color: agent.avatar_color,
        is_enabled: agent.is_enabled,
        availability_status: agent.availability_status,
        current_work: null,
        workload: null,
        awaiting_operator: null,
        specialist_readiness: agent.specialties
          ? { declared_specialties: agent.specialties, has_outcome_evidence: false, evidence: null, verified_success_rate: null }
          : null,
        attention_count: null,
        live: false,
      }));
    }
    return [];
  }, [employees, agentsQuery.data]);

  const systemConnections = useMemo(() => {
    if (!connections) return null;
    return [
      { key: "openclaw", label: "OpenClaw (ביצוע כלים)", configured: connections.openclaw?.configured === true, note: connections.openclaw?.mode_label || null },
      { key: "teamviewer", label: "TeamViewer (שליטה מרחוק)", configured: connections.teamviewer?.configured === true, note: null },
      { key: "ms365", label: "Microsoft 365 (דואר וזהות)", configured: connections.ms365?.configured === true, note: null },
      { key: "ai_analysis", label: "מנוע ניתוח AI", configured: connections.ai_analysis?.configured === true, note: connections.ai_analysis?.provider || null },
    ];
  }, [connections]);
  const configuredCount = systemConnections ? systemConnections.filter((system) => system.configured).length : null;

  const stageStates = useMemo(() => {
    const handlingNow = employees.filter((employee) => employee.current_work).length;
    const openConversations = Array.isArray(conversationsQuery.data)
      ? conversationsQuery.data.filter((conversation) => conversation.status !== "closed").length
      : null;
    const knowledgePresent = Array.isArray(knowledgeQuery.data) && knowledgeQuery.data.length > 0;
    const declaredCount = employees.filter(
      (employee) => (employee.specialist_readiness?.declared_specialties || []).length > 0
    ).length;
    const approvals = approvalsQuery.data || null;
    const gaps = gapsQuery.data || null;

    const states = {};

    states.intake = openTicketsQuery.isLoading && !openTicketsQuery.data
      ? { status: "loading", summary: "טוען…" }
      : Array.isArray(openTicketsQuery.data)
        ? openTicketsQuery.data.length === 0
          ? { status: "idle", summary: "אין קריאות פתוחות" }
          : { status: "active", summary: `קריאות פתוחות: ${openTicketsQuery.data.length}${openTicketsQuery.data.length >= OPEN_TICKET_CAP ? "+" : ""}` }
        : { status: "unknown", summary: "המקור לא נטען" };

    states.routing = workforceQuery.isLoading && !workforce
      ? { status: "loading", summary: "טוען…" }
      : !workforce
        ? { status: "unknown", summary: "המקור לא נטען" }
        : handlingNow > 0
          ? { status: "active", summary: `מטפלים כעת: ${handlingNow}` }
          : (totals?.awaiting_operator || 0) > 0
            ? { status: "approval_required", summary: `ממתינים למפעיל: ${totals.awaiting_operator}` }
            : { status: "idle", summary: "אין עבודות פעילות כעת" };

    states.conversation = conversationsQuery.isLoading && !conversationsQuery.data
      ? { status: "loading", summary: "טוען…" }
      : Array.isArray(conversationsQuery.data)
        ? openConversations > 0
          ? { status: "active", summary: `שיחות פתוחות: ${openConversations}` }
          : { status: "idle", summary: conversationsQuery.data.length ? "אין שיחות פתוחות" : "אין שיחות אחרונות" }
        : { status: "unknown", summary: "המקור לא נטען" };

    const knowledgeFailed = knowledgeQuery.isError && !knowledgeQuery.data && !workforce;
    states.skills = knowledgeQuery.isLoading && !knowledgeQuery.data && !workforce
      ? { status: "loading", summary: "טוען…" }
      : knowledgeFailed
        ? { status: "unknown", summary: "המקור לא נטען" }
        : declaredCount > 0
          ? { status: "active", summary: `עובדים עם תחומים מוצהרים: ${declaredCount}` }
          : knowledgePresent
            ? { status: "active", summary: "בסיס ידע מאושר זמין" }
            : Array.isArray(knowledgeQuery.data)
              ? { status: "idle", summary: "טרם הוצהרו תחומים" }
              : { status: "unknown", summary: "בסיס הידע לא נטען" };

    states.connections = connectionsQuery.isLoading && !connections
      ? { status: "loading", summary: "טוען…" }
      : !systemConnections
        ? { status: "unknown", summary: "המקור לא נטען" }
        : configuredCount === systemConnections.length
          ? { status: "active", summary: `מוכנות: ${configuredCount}/${systemConnections.length}` }
          : configuredCount > 0
            ? { status: "requires_configuration", summary: `מוכנות: ${configuredCount}/${systemConnections.length}` }
            : { status: "not_connected", summary: "אין חיבור מוגדר" };

    states.approval = approvalsQuery.isLoading && !approvals
      ? { status: "loading", summary: "טוען…" }
      : !approvals
        ? { status: "unknown", summary: "המקור לא נטען" }
        : approvals.pending_count > 0
          ? { status: "approval_required", summary: `ממתינים לאישור: ${approvals.pending_count}` }
          : { status: "idle", summary: "אין אישורים ממתינים" };

    if (crisis?.active === true) {
      states.execution = { status: "blocked", summary: "חסום — מצב חירום פעיל" };
    } else if (aiSettings && aiSettings.ai_technician_pilot_enabled === false) {
      states.execution = { status: "disabled", summary: "כבוי — פיילוט AI Technician לא פעיל" };
    } else if ((totals?.active_runs || 0) > 0) {
      states.execution = { status: "active", summary: `ריצות בפעולה: ${totals.active_runs}` };
    } else if (!systemConnections) {
      states.execution = { status: "unknown", summary: "מצב החיבורים לא נטען" };
    } else {
      const tools = Array.isArray(connections?.tool_registry) ? connections.tool_registry : [];
      const allRequireNode = tools.length > 0 && tools.every((tool) => tool.status === "requires_connected_node");
      states.execution = tools.length === 0
        ? { status: "no_evidence", summary: "אין כלי ביצוע רשומים" }
        : allRequireNode
          ? { status: "requires_configuration", summary: "דורש צומת OpenClaw מחובר" }
          : { status: "idle", summary: "מוכן — אין ריצות פעילות" };
    }

    states.verification = workforceQuery.isLoading && !workforce
      ? { status: "loading", summary: "טוען…" }
      : !workforce
        ? { status: "unknown", summary: "המקור לא נטען" }
        : { status: "active", summary: `אומתו: ${totals.verified_successes} · טרם אומתו: ${totals.unverified_completions} · כשלונות: ${totals.failures}` };

    const gapsKnown = Array.isArray(gaps);
    const feedbackKnown = feedbackQuery.data || null;
    states.learning = gapsQuery.isLoading && !gapsKnown && !feedbackKnown && !knowledgePresent
      ? { status: "loading", summary: "טוען…" }
      : feedbackKnown
        ? { status: "active", summary: `תיקוני מפעילים: ${feedbackKnown.total_edits}` }
        : gapsKnown && gaps.length > 0
          ? { status: "active", summary: `פערי ידע: ${gaps.length}` }
          : knowledgePresent
            ? { status: "idle", summary: "אין ראיות למידה חדשות בחלון" }
            : { status: "unknown", summary: "המקור לא נטען" };

    return states;
  }, [
    openTicketsQuery.data, openTicketsQuery.isLoading,
    workforceQuery.isLoading, workforce,
    conversationsQuery.data, conversationsQuery.isLoading,
    knowledgeQuery.data, knowledgeQuery.isLoading, knowledgeQuery.isError,
    gapsQuery.data, gapsQuery.isLoading,
    feedbackQuery.data,
    approvalsQuery.data, approvalsQuery.isLoading,
    connectionsQuery.isLoading, connections, systemConnections, configuredCount,
    crisis, aiSettings, totals, employees,
  ]);

  // A stage flashes only when a successful refresh genuinely changed its live status/summary —
  // never on the first paint, never while loading, never when a source failed (a failure is not
  // activity). The change itself is computed in both motion modes; prefers-reduced-motion swaps
  // the flash for a static text equivalent below instead of silently dropping the information.
  const previousStageStatesRef = useRef(null);
  const [changedStages, setChangedStages] = useState({});
  useEffect(() => {
    const previous = previousStageStatesRef.current;
    previousStageStatesRef.current = stageStates;
    if (!previous) return undefined;
    const changed = {};
    let changedAny = false;
    for (const stageId of Object.keys(stageStates)) {
      const before = previous[stageId];
      const after = stageStates[stageId];
      if (!before || !after) continue;
      // A stage emerging from loading/a failed source is arrival of truth, not a change; and a
      // failure itself is not activity. Only a change between two established live states flashes.
      if (before.status === "loading" || before.status === "unknown") continue;
      if (after.status === "loading" || after.status === "unknown") continue;
      if (before.status === after.status && before.summary === after.summary) continue;
      changed[stageId] = true;
      changedAny = true;
    }
    if (!changedAny) return undefined;
    setChangedStages(changed);
    const timer = setTimeout(() => setChangedStages({}), LIVE_CHANGE_FLASH_MS);
    return () => clearTimeout(timer);
  }, [stageStates]);
  const flashStageIds = prefersReducedMotion ? {} : changedStages;
  const changedStageLabels = prefersReducedMotion
    ? STAGES.filter((stage) => changedStages[stage.id]).map((stage) => stage.label)
    : [];

  const ctx = {
    openTickets: openTicketsQuery.data || null,
    openTicketsQuery,
    workforce,
    workforceQuery,
    totals,
    employees,
    attention,
    conversations: conversationsQuery.data || null,
    conversationsQuery,
    knowledge: knowledgeQuery.data || null,
    knowledgeQuery,
    connections,
    connectionsQuery,
    systemConnections,
    configuredCount,
    approvals: approvalsQuery.data || null,
    approvalsQuery,
    failures: failuresQuery.data || null,
    failuresQuery,
    crisis,
    crisisQuery,
    crisisKnown: !!crisis,
    aiSettings,
    aiSettingsQuery,
    entitlement,
    entitlementsQuery,
    gaps: gapsQuery.data || null,
    gapsQuery,
    feedback: feedbackQuery.data || null,
    feedbackQuery,
    canManage,
    isAdmin,
    handlingNow: employees.filter((employee) => employee.current_work).length,
  };

  const permissionBlock = useMemo(() => {
    if (permsLoading) return <div className="p-6 text-gray-500">טוען הרשאות…</div>;
    if (!canView) {
      return <div className="p-6 text-gray-500">אין הרשאה לצפות במפת המערכת — נדרשת הרשאת צפייה בעובדים דיגיטליים</div>;
    }
    return null;
  }, [permsLoading, canView]);

  const newestUpdate = Math.max(
    0,
    agentsQuery.dataUpdatedAt || 0,
    workforceQuery.dataUpdatedAt || 0,
    approvalsQuery.dataUpdatedAt || 0,
    connectionsQuery.dataUpdatedAt || 0
  );
  const liveSources = [
    workforceQuery, openTicketsQuery, conversationsQuery, approvalsQuery,
    failuresQuery, connectionsQuery, knowledgeQuery, eventFlowQuery,
  ];
  const anySourceFailed = liveSources.some((query) => sourceFailed(query));
  const anySourceStale = liveSources.some((query) => isSourceStale(query));

  // The reconnect cue (DIGEMP-CLOSEOUT-SYSTEM-MAP-UX-209): when the browser network returns
  // after a real offline period, every read-only source is refetched so the held offline snapshot
  // becomes live again on its own. The shared hook fires only on an actual offline→online
  // transition, never on first mount, never while the page is not permitted to read, and it only
  // invokes query refetchers — no mutation or action can be replayed by a reconnect.
  const mapQueriesRef = useRef(null);
  mapQueriesRef.current = [
    agentsQuery, workforceQuery, openTicketsQuery, conversationsQuery, approvalsQuery,
    failuresQuery, connectionsQuery, knowledgeQuery, crisisQuery, entitlementsQuery,
    aiSettingsQuery, gapsQuery, feedbackQuery, eventFlowQuery,
  ];
  useReconnectRefetch(
    [() => mapQueriesRef.current.forEach((query) => query.refetch())],
    { enabled: canView }
  );

  const refetchAll = () => {
    mapQueriesRef.current.forEach((query) => query.refetch());
  };

  if (permissionBlock) return permissionBlock;

  const activeStageIndex = STAGES.findIndex((stage) => stage.id === activeStage);
  const activeStageDef = STAGES[activeStageIndex];
  const selectedNode = employeeNodes.find((node) => node.agent_id === selectedAgentId) || null;

  const onTabsKeyDown = (event) => {
    let nextIndex = null;
    if (event.key === "ArrowLeft") nextIndex = (activeStageIndex + 1) % STAGES.length; // RTL: left is next
    else if (event.key === "ArrowRight") nextIndex = (activeStageIndex - 1 + STAGES.length) % STAGES.length;
    else if (event.key === "Home") nextIndex = 0;
    else if (event.key === "End") nextIndex = STAGES.length - 1;
    if (nextIndex === null) return;
    event.preventDefault();
    setActiveStage(STAGES[nextIndex].id);
    document.getElementById(`system-map-tab-${STAGES[nextIndex].id}`)?.focus();
  };

  return (
    <div className="w-full max-w-[1200px] mx-auto px-4 py-6 space-y-5" dir="rtl">
      <header className="space-y-1">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <h1 className="text-xl font-semibold text-gray-800">איך זה עובד — מפת המערכת החיה</h1>
          <button
            type="button"
            onClick={refetchAll}
            className="text-sm border border-gray-300 rounded-lg px-3 py-1.5 hover:bg-gray-50"
          >
            רענן
          </button>
        </div>
        <p className="text-sm text-gray-500">
          כל שלב במסלול העבודה של העובדים הדיגיטליים — עם מצב חי ממקורות הנתונים הקיימים. המפה מסבירה בלבד:
          אין בה הרשאות, הפעלת ביצוע או עקיפת אישורים.
        </p>
        {newestUpdate > 0 && (
          <p className="text-xs text-gray-400">
            נטען לאחרונה: {new Date(newestUpdate).toLocaleTimeString("he-IL")}
            {anySourceFailed && " · חלק מהמקורות לא נטענו — יוצג לא ידוע"}
            {anySourceStale && " · חלק מהמקורות לא התרעננו — מוצג הנתון מהטעינה האחרונה שהצליחה"}
          </p>
        )}
      </header>

      {!isOnline && (
        <p role="status" className="rounded-lg border border-amber-300 bg-amber-50 text-amber-900 text-sm p-3">
          אין חיבור לרשת — לא ניתן לרענן את מקורות המפה כרגע; מוצג הנתון מהטעינות האחרונות שהצליחו.
          עם חזרת החיבור כל מקורות המפה ירועננו אוטומטית.
        </p>
      )}

      {canManage && crisis?.active === true && (
        <div role="alert" className="rounded-lg border border-red-300 bg-red-50 text-red-900 text-sm p-3 space-y-1">
          <p className="font-semibold">מצב חירום פעיל</p>
          <p>כל פעולות הכתיבה אמורות להיות חסומות. הנתון מדווח מהשרת; אכיפת חסימה מלאה על כל מתאמי הכתיבה טרם אומתה.</p>
        </div>
      )}
      {canManage && sourceFailed(crisisQuery) && (
        <div className="rounded-lg border border-gray-300 bg-gray-50 text-gray-700 text-sm p-3">
          מצב החירום לא נטען — מצב החסימה אינו ידוע.
        </div>
      )}

      <section aria-label="שלושת הממדים" className="grid gap-3 md:grid-cols-3">
        <div className="rounded-xl border border-violet-200 bg-violet-50/60 p-3 space-y-1">
          <h2 className="text-sm font-semibold text-violet-800">יכולת (Capability)</h2>
          <p className="text-xs text-gray-600">מה העובד יודע לעשות: תחומים מוצהרים, מיומנויות וניסיון מתועד. הצהרה אינה מדידה ואינה הרשאה.</p>
        </div>
        <div className="rounded-xl border border-emerald-200 bg-emerald-50/60 p-3 space-y-1">
          <h2 className="text-sm font-semibold text-emerald-800">הרשאה (Permission)</h2>
          <p className="text-xs text-gray-600">מה מותר לעובד לעשות: רמת הרשאה, רשימת כלים מאושרת וזכאות כלים. הרשאה אינה ביצוע — פעולה חיצונית עוברת אישור ובקרה.</p>
        </div>
        <div className="rounded-xl border border-sky-200 bg-sky-50/60 p-3 space-y-1">
          <h2 className="text-sm font-semibold text-sky-800">מוכנות כעת (Readiness)</h2>
          <p className="text-xs text-gray-600">מה העובד יכול לעשות ברגע זה: מופעל/כבוי, המתנות, חיבורים שנבדקו ומצב חירום. עובד יכול להיות מסוגל ומורשה — ובלי חיבור או תחת מצב חירום הוא לא מוכן כרגע.</p>
        </div>
      </section>

      <section aria-label="שלבי הזרימה" className="space-y-3">
        {changedStageLabels.length > 0 && (
          <p data-testid="system-map-changed-static" role="status" className="text-xs text-gray-500">
            מצב השלבים עודכן זה עתה: {changedStageLabels.join(" · ")} — מוצג כטקסט במקום הבהוב (העדפת צמצום תנועה).
          </p>
        )}
        <div role="tablist" aria-label="שלבי הזרימה" onKeyDown={onTabsKeyDown} className="flex flex-wrap gap-2">
          {STAGES.map((stage) => {
            const state = stageStates[stage.id] || { status: "unknown", summary: "" };
            const isActive = activeStage === stage.id;
            const isChanged = flashStageIds[stage.id] === true;
            return (
              <button
                key={stage.id}
                id={`system-map-tab-${stage.id}`}
                type="button"
                role="tab"
                aria-selected={isActive}
                aria-controls="system-map-stage-panel"
                tabIndex={isActive ? 0 : -1}
                onClick={() => setActiveStage(stage.id)}
                data-live-changed={isChanged ? "true" : undefined}
                className={`flex flex-col items-center gap-1 rounded-xl border px-3 py-2.5 min-w-[7.5rem] text-center motion-safe:transition-colors ${
                  isActive ? "border-violet-400 bg-violet-50 shadow-sm" : "border-gray-200 bg-white hover:border-violet-200"
                }${isChanged ? " live-state-changed" : ""}`}
              >
                <span aria-hidden="true" className="text-lg">{stage.icon}</span>
                <span className="text-sm font-medium text-gray-800">{stage.label}</span>
                <StatusChip status={state.status} />
                <span className="text-[11px] text-gray-500">{state.summary}</span>
              </button>
            );
          })}
        </div>
        <div
          id="system-map-stage-panel"
          role="tabpanel"
          aria-labelledby={`system-map-tab-${activeStage}`}
          aria-busy={stageStates[activeStage]?.status === "loading"}
          className="rounded-xl border border-gray-200 bg-white shadow-sm p-4"
        >
          <h2 className="text-base font-semibold text-gray-800 mb-2">
            {activeStageDef.icon} {activeStageDef.label}
          </h2>
          <StageDetail stageId={activeStage} ctx={ctx} />
        </div>
      </section>

      <EventFlowSection
        flow={eventFlow}
        query={eventFlowQuery}
        flowFrozen={eventFlowFrozen}
        flowOffline={flowOffline}
        prefersReducedMotion={prefersReducedMotion}
      />

      <FocusSection
        flow={eventFlow}
        query={eventFlowQuery}
        flowFrozen={eventFlowFrozen}
        flowOffline={flowOffline}
        approvals={approvalsQuery.data || null}
        approvalsQuery={approvalsQuery}
        attention={attention}
        workforceQuery={workforceQuery}
        agentsQuery={agentsQuery}
        crisisQuery={crisisQuery}
        employees={employees}
        employeeNodes={employeeNodes}
        crisisActive={crisis?.active === true}
        canManage={canManage}
      />

      <section
        aria-label="העובדים הדיגיטליים"
        aria-busy={agentsQuery.isLoading || workforceQuery.isLoading}
        className="space-y-3"
      >
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <h2 className="text-lg font-semibold text-gray-800">העובדים הדיגיטליים</h2>
          <p className="text-xs text-gray-500">לחצו על עובד כדי לפתוח תצוגת עומק: יכולת, הרשאה ומוכנות כעת.</p>
        </div>
        {workforceQuery.isError && employees.length === 0 && agentsQuery.data && (
          <div className="rounded-lg border border-gray-300 bg-gray-50 text-gray-700 text-sm p-3">
            הסטטוס החי של העובדים לא נטען — מוצגת הרשומה הבסיסית בלבד, ומצב המוכנות יוצג כלא ידוע.
          </div>
        )}
        {workforceQuery.isError && employees.length === 0 && !agentsQuery.data && (
          <FailNote>רשימת העובדים לא נטענה — לא מוצג ניחוש.</FailNote>
        )}
        {employeeNodes.length === 0 && !workforceQuery.isError && (
          <p role="status" className="text-sm text-gray-400">{agentsQuery.isLoading || workforceQuery.isLoading ? "טוען…" : "אין עובדים דיגיטליים מוגדרים"}</p>
        )}
        <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
          {employeeNodes.map((node) => {
            const readiness = employeeReadinessChip(node, { crisisActive: crisis?.active === true });
            const isSelected = selectedAgentId === node.agent_id;
            return (
              <button
                key={node.agent_id}
                id={`system-map-employee-${node.agent_id}`}
                type="button"
                onClick={() => setSelectedAgentId(isSelected ? null : node.agent_id)}
                aria-pressed={isSelected}
                aria-label={`פתח פרטי עובד: ${node.name}`}
                className={`text-right w-full rounded-xl border bg-white p-3 space-y-2 shadow-sm motion-safe:transition-colors ${
                  isSelected ? "border-violet-400 ring-2 ring-violet-300" : "border-gray-200 hover:border-violet-300"
                }`}
              >
                <span className="flex items-center gap-3">
                  <EmployeeAvatar
                    name={node.name}
                    avatarKey={node.avatar_key}
                    avatarColor={node.avatar_color}
                    status={node.availability_status}
                    isEnabled={node.is_enabled}
                    size="md"
                    showStatus
                  />
                  <span className="min-w-0">
                    <span className="block font-medium text-gray-800 truncate">{node.name}</span>
                    {node.role_title && <span className="block text-xs text-gray-500 truncate">{node.role_title}</span>}
                  </span>
                </span>
                <span className="flex flex-wrap gap-1">
                  <DimensionChip dimension="capability">{capabilityText(node)}</DimensionChip>
                  <DimensionChip dimension="permission">{permissionText(agentsById.get(node.agent_id))}</DimensionChip>
                  <DimensionChip dimension="readiness">{readiness.label}</DimensionChip>
                </span>
                {node.live ? (
                  <span className="block text-[11px] text-gray-400 flex flex-wrap gap-x-2">
                    <span>
                      {(node.workload?.active_runs || 0) > 0
                        ? `עבודות פעילות: ${node.workload.active_runs}`
                        : "פנוי כעת"}
                      {(node.awaiting_operator || 0) > 0 && ` · ממתין למפעיל: ${node.awaiting_operator}`}
                    </span>
                    {(node.attention_count || 0) > 0 && <span className="text-amber-700">דורש תשומת לב: {node.attention_count}</span>}
                  </span>
                ) : (
                  <span className="block text-[11px] text-gray-400">הסטטוס החי לא נטען — מוכנות תוצג כלא ידוע</span>
                )}
              </button>
            );
          })}
        </div>
        {selectedNode && (
          <EmployeeDrillDown
            node={selectedNode}
            agent={agentsById.get(selectedNode.agent_id) || null}
            workforceEmployee={workforceQuery.data ? employees.find((employee) => employee.agent_id === selectedNode.agent_id) || null : null}
            entitlementAgent={canManage && entitlement ? entitlement.agents.find((entry) => entry.agent_id === selectedNode.agent_id) || null : null}
            entitlementMeta={canManage && entitlement ? { enforcement_currently_enabled: entitlement.enforcement_currently_enabled } : null}
            queries={{ workspaceQuery, growthQuery, runsQuery }}
            crisisActive={crisis?.active === true}
            canManage={canManage}
            onClose={() => {
              // Keyboard recovery: the closed drill-down's button disappears with it, so focus
              // returns to the tile that opened it instead of dropping to the document body.
              setSelectedAgentId(null);
              document.getElementById(`system-map-employee-${selectedNode.agent_id}`)?.focus();
            }}
          />
        )}
      </section>

      <footer className="text-xs text-gray-400 space-y-1">
        <p>
          כל תוויות המצב במפה נגזרות ממקורות הנתונים הקיימים של המערכת; אין במפה הרשאות, ביצוע או אישורים משל עצמה.
          מקור לא זמין מוצג כ"לא ידוע" — לעולם לא כמספר מומצא.
        </p>
        <p>
          מסכים משלימים:{" "}
          <Link className="text-blue-600 hover:underline" to="/agent-runs">ריצות AI</Link>
          {" · "}
          <Link className="text-blue-600 hover:underline" to="/attention-inbox">תיבת תשומת לב</Link>
          {" · "}
          <Link className="text-blue-600 hover:underline" to="/connections">חיבורים</Link>
          {" · "}
          <Link className="text-blue-600 hover:underline" to="/agents">העובדים</Link>
        </p>
      </footer>
    </div>
  );
}
